import { IMPORT_SOURCES } from "./import.jsx";
import React from "react";
import { cbmFormatWhatsApp, CARD_LANGUAGE_NAMES, CARD_LANGUAGES, gameLabel, GAMES, GRADE_NAMES, GRADES, RADIUS_OPTIONS, shortCount} from "./data.jsx";
import { api, suggestionNameFor } from "./api.jsx";
import { currentState, enable, disable, deviceLabel, showLocalTestNotification, workerVersion, pushDiagnosis } from "./push.jsx";
import { useApp } from "./store.jsx";
import { LocationPicker } from "./location.jsx";
import { CardAutocomplete } from "./autocomplete.jsx";
import { PhotoFields } from "./photos.jsx";
import { ModalHost, LanguageSwitch, StatRow, SupportDialog, LoadMore, ListingGridSkeleton, WantRowsSkeleton, GamePicker, CardLanguagePicker, Linked, MarketFollowFields, percentTextFromBp, BulkBar, Dialog, ConfirmDialog, useConfirm } from "./chrome.jsx";

const { Button, IconButton, Icon, Badge, Card, Tabs, Input, Select, Switch, Checkbox, ListingCard, EmptyState, FluteDivider, ConditionChip, DistanceTag } = window.CardboardmarketDesignSystem_2a3239;

/**
 * ONE SELECTION GESTURE, used by both lists on this screen.
 *
 * Asked for as: "when nothing is selected, then you must click the checkbox
 * exactly to start multi-selection mode. But once we're in that mode, we should
 * be able to click any part of the listing box to check/uncheck it. Also, we
 * should allow some usual behaviors like click once then shift-click another
 * listing spaced out and automatically select every listing in between" — and
 * then, of the wants: "same thing".
 *
 * THERE IS NO MODE FLAG. Selecting anything IS the mode, so there is nothing to
 * enter and nothing to leave: clearing the last tick hands the cards back to
 * being cards. A toggle would be a third state to explain, and a control that
 * has to be switched off before an ordinary click works again.
 *
 * ONE HANDLER, TAKEN IN THE CAPTURE PHASE, rather than a click on the row and a
 * change on the checkbox. A tap on a label is two events for one gesture — the
 * label's click and the input's change — and reacting to both toggles twice.
 * Capturing at the top of the cell means the inner control never sees the click
 * at all, so the box is drawn purely from the selection it is told about, and
 * the gesture is read once, with its modifier keys intact. (The design system's
 * Checkbox hands `onChange` a boolean, not an event, so a shift-click is
 * invisible from there — see `Input` and `Select` disagreeing about the same
 * thing elsewhere.)
 *
 * A BUTTON IS STILL A BUTTON. "Any part of the box" means the parts that are
 * not controls: Edit, Take it down, the × on a want and the "{n} nearby" that
 * opens a search keep doing their own jobs while a selection is live. Losing
 * those would be trading one convenience for a screen whose buttons stop
 * working.
 */
function useSelection(items) {
  const [picked, setPicked] = React.useState(() => new Set());
  /*
   * Where a shift-click measures FROM — the last box pressed on its own.
   *
   * Held as an id rather than an index because the list grows under it with
   * "Show more", and it survives a shift-click rather than moving to it: that
   * is what lets somebody widen and narrow a range from the same end, which is
   * how every file list behaves.
   */
  const [anchor, setAnchor] = React.useState(null);
  const chosen = items.filter((i) => picked.has(i.id));
  const selecting = picked.size > 0;

  const clear = () => { setPicked(new Set()); setAnchor(null); };
  const selectAll = () => setPicked(new Set(items.map((i) => i.id)));

  /**
   * Returns true when the gesture was the selection's, so the caller knows the
   * click has been spent and nothing else should happen to it.
   */
  const press = (id, event) => {
    const target = event.target;
    const within = (sel) => Boolean(target && target.closest && target.closest(sel));
    const onBox = within('.cbm-pick, .cbm-pick-card');
    // A control keeps its own job, and the tick box is never one of those: it
    // is inside `.cbm-pick`, so this question is asked second.
    if (!onBox && (!selecting || within('button'))) return false;

    event.preventDefault();
    event.stopPropagation();

    if (event.shiftKey && anchor && anchor !== id) {
      const from = items.findIndex((i) => i.id === anchor);
      const to = items.findIndex((i) => i.id === id);
      if (from >= 0 && to >= 0) {
        const span = items.slice(Math.min(from, to), Math.max(from, to) + 1).map((i) => i.id);
        // ADDED to what is already ticked rather than replacing it. Somebody who
        // has picked three cards by hand and then shift-picks a run meant both.
        setPicked((was) => new Set([...was, ...span]));
        return true;
      }
    }
    setPicked((was) => {
      const next = new Set(was);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setAnchor(id);
    return true;
  };

  return { picked, setPicked, chosen, selecting, anchor, clear, selectAll, press };
}

/**
 * WHAT A WANT WILL ACCEPT, in one place — migration 137.
 *
 * ONE COMPONENT BECAUSE THERE ARE TWO FORMS. The sidebar builds a want from
 * scratch and the dialog confirms one handed over from a search, and both ask
 * the same question. They each had their own copy of the condition select
 * before this, which is how the two came to disagree within an hour: the
 * sidebar's was rewired to the column that now exists and the dialog's was left
 * writing into the note, where nothing reads it.
 *
 * THE CONDITION IS A FLOOR and the caption says so. "I want this in NM" never
 * means "and not in Mint" — somebody naming a condition is saying how bad they
 * will tolerate.
 *
 * THE PRINTING IS ONLY OFFERED WHEN ONE WAS CHOSEN. Typing a name freely names
 * a card and never an edition, so a suggestion somebody picked is the only way
 * the app knows a set at all. Hidden rather than disabled: a disabled tick box
 * invites somebody to work out what would enable it, and its absence says the
 * same thing without the puzzle.
 */
function WantConditions({ minGrade, setMinGrade, language, setLanguage, foil, setFoil, productId, exactPrinting, setExactPrinting }) {
  const { t } = useApp();
  return (
    <>
      <Select
        label={t("Worst condition you would take")}
        options={[{ value: '', label: t("Any condition") }].concat(
          GRADES.map((g) => ({ value: g, label: `${g} · ${t(GRADE_NAMES[g])}` })),
        )}
        value={minGrade}
        onChange={setMinGrade}
      />
      <Select
        /* "Card language", the words browse already uses, and not "Language":
           the header carries an INTERFACE language switch, so a bare "Idioma"
           in a sidebar is ambiguous to a reader before it is to a test. */
        label={t("Card language")}
        options={[{ value: '', label: t("Any language") }].concat(
          CARD_LANGUAGES.map((l) => ({ value: l, label: t(CARD_LANGUAGE_NAMES[l] || l) })),
        )}
        value={language}
        onChange={setLanguage}
      />
      <Select
        label={t("Foil")}
        options={[
          { value: '', label: t("Foil or not") },
          { value: 'foil', label: t("Foil only") },
          { value: 'plain', label: t("Non-foil only") },
        ]}
        value={foil}
        onChange={setFoil}
      />
      {productId ? (
        <Checkbox
          label={t("Only this exact printing")}
          checked={exactPrinting}
          /* A boolean, not an event — see the note at the top of this file.
             Checkbox and Input disagree about this and always have. */
          onChange={setExactPrinting}
        />
      ) : null}
    </>
  );
}

function WantsPanel() {
  const { route, navigate, wants, wantsState, addWant, removeWant, removeWants, listings, radiusKm, cursors, loadMore, loadingMore, go, t } = useApp();
  const [title, setTitle] = React.useState('');
  const [game, setGame] = React.useState('onepiece');
  /*
   * NO NOTE STATE ANY MORE. The only control that wrote to it was the condition
   * select, which asked for a grade and stored it in a field nothing read; it
   * points at `min_grade` now. A want still HAS a note — the Cardmarket import
   * puts the trader's own list name there, "EDH - Dragons", which is the one
   * thing in that file saying WHY they want the card — but nothing on this form
   * writes one, so holding an always-empty string here was state pretending to
   * be a feature.
   */
  // Set when the name came from the suggestions. It is what lets the want follow
  // the card instead of the spelling — see migration 025.
  const [productId, setProductId] = React.useState(null);
  // The picked card's English name, beside `productId` (the box may show another language's).
  const [pickedName, setPickedName] = React.useState(null);
  /*
   * HOW SPECIFIC THIS WANT IS — migration 137.
   *
   * The owner: "the user should be able to specify not only the card name, but
   * also the set it belongs to, condition, language and foil/no foil... all
   * those conditions could be relaxed by including an 'Any' option."
   *
   * EMPTY STRING IS "ANY" in all three, and never travels: `submit` drops them
   * rather than sending null, because absent and null mean the same thing to
   * the API and absent is the one that says "nobody narrowed this".
   */
  const [minGrade, setMinGrade] = React.useState('');
  const [language, setLanguage] = React.useState('');
  const [foil, setFoil] = React.useState('');
  /*
   * ONLY MEANINGFUL WITH A PRINTING TO PIN TO, and the API refuses it without
   * one. Reset whenever the card is retyped, below, because a pin that outlives
   * the printing it pointed at is a want narrowed to a card nobody chose.
   */
  const [exactPrinting, setExactPrinting] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const submit = () => {
    if (!title.trim()) return;
    addWant({
      // A picked card is wanted under its English name, which the title match
      // keys on (lib/want-matches.ts); the box shows the name it was found by.
      title: (productId && pickedName) || title.trim(), game,
      ...(productId ? { sourceProductId: productId } : {}),
      // Sent only when answered. See the state above on why absent, not null.
      ...(minGrade ? { minGrade } : {}),
      ...(language ? { language } : {}),
      ...(foil ? { foil: foil === 'foil' } : {}),
      ...(exactPrinting && productId ? { exactPrinting: true } : {}),
    });
    setTitle(''); setProductId(null); setPickedName(null);
    setMinGrade(''); setLanguage(''); setFoil(''); setExactPrinting(false);
    clearHandover();
  };

  /*
   * A search carried over from browse.
   *
   * Browse sends the card it could not find; this fills the form with it and opens
   * a confirmation, so the person sees exactly what is about to be saved and can
   * change the game or add a note first. The alternative — saving it the moment
   * they tapped — put a card's name on a button, which does not fit, and acted
   * before anybody agreed.
   *
   * The parameters are cleared once read, with `replace`, so the back button does
   * not walk into a form that fills itself in again.
   */
  /*
   * SEVERAL AT ONCE, HELD BY ID.
   *
   * By id and not by index, because this list GROWS under the selection: "Show
   * more" appends a page, and a set of row numbers would quietly come to mean
   * different wants than the ones that were ticked.
   *
   * The count the bar shows is then derived from the rows actually on screen
   * rather than from the set's size, so a want that left the list — removed in
   * another tab, or by the refetch after a removal here — stops being counted
   * without anything having to remember to prune it.
   */
  const { picked, chosen, selecting, clear, selectAll, press } = useSelection(wants);
  const [removing, setRemoving] = React.useState(false);
  /*
   * ASKED FIRST, both of them — the owner, 2026-10-23: "I just deleted some
   * wants by mistake." A removed want is gone with its conditions and the
   * note an import put on it, and nothing on this screen brings it back.
   */
  const [confirm, confirmDialog] = useConfirm();
  const removeOne = async (w) => {
    const yes = await confirm({
      title: t("Remove “{card}” from your wants?", { card: w.localTitle || w.title }),
      description: t("We stop watching for it. Adding it again means filling the form again."),
      confirmLabel: t("Remove want"),
    });
    if (yes) removeWant(w.id);
  };
  const removeChosen = async () => {
    if (!chosen.length || removing) return;
    const ids = chosen.map((w) => w.id);
    const yes = await confirm({
      title: ids.length === 1 ? t("Remove 1 want?") : t("Remove {n} wants?", { n: ids.length }),
      description: t("We stop watching for them. Adding them again means filling the form again."),
      confirmLabel: ids.length === 1 ? t("Remove 1 want") : t("Remove {n} wants", { n: ids.length }),
      items: chosen.map((w) => w.localTitle || w.title),
      'data-cbm': 'confirm-remove-wants',
    });
    if (!yes) return;
    setRemoving(true);
    try {
      await removeWants(ids);
      clear();
    } catch {
      // Said by the store, and said better than a second copy here: the rows
      // are back on screen, which is most of the message.
    } finally { setRemoving(false); }
  };

  const handover = route.want;
  const clearHandover = React.useCallback(() => {
    if (!route.want && !route.wantGame && !route.wantProduct) return;
    navigate('you', { tab: 'wants' }, { replace: true });
  }, [route.want, route.wantGame, route.wantProduct, navigate]);

  React.useEffect(() => {
    if (!handover) return;
    setTitle(handover);
    if (route.wantGame) setGame(route.wantGame);
    setProductId(route.wantProduct ? Number(route.wantProduct) : null);
    setPickedName(null);
    setConfirming(true);
  }, [handover, route.wantGame, route.wantProduct]);
  return (
    <div className="cbm-split cbm-split--wants">
      <div className="cbm-stack" style={{ gap: 0 }}>
        {/*
          WHAT TO DO WITH THE ONES THAT ARE TICKED.

          AT THE TOP AND STUCK THERE, asked for in those words: "the action
          controls should be at the top and should be sticky so they are always
          shown even if we're scrolling a lot." Which is the case this exists
          for — somebody tidying an imported wants list has fifty rows, and a
          bar at the far end of them is a bar you have to go and find after
          every tick.

          AND ITS SPACE IS HELD WHETHER OR NOT ANYTHING IS TICKED, which is the
          second half of the same report: "let's reserve the spacing so there's
          no layout shift". Rendered only when something was selected, the first
          tick pushed the whole list down — moving the control that had just
          been tapped, out from under the finger that tapped it.

          AND IT IS SHOWN WHILE IT WAITS, which the first version got wrong:
          "now we're reserving the space of the multiple selection toolbar
          without showing it until there's at least one item selected. It feels
          weird." It did — a toolbar-shaped gap holding nothing, above a list.
          The space is still reserved by the real bar rather than by a spacer of
          some copied height: a `min-height` would need re-measuring for every
          breakpoint and every translation, and would be wrong in Spanish the
          day a label grew. What changed is that the bar is now dimmed instead
          of invisible.
          Nothing had to move to allow it. The buttons inside were already
          `disabled` with nothing ticked, so they stay out of the tab order on
          their own — which is what the hiding was doing for them — and the bar
          is not dead while it waits: "Select all {n}" is live, and it is the
          one action that makes sense before anything is chosen.

          Which is why nothing is conditional inside it either. "Select the {n}
          shown" is DISABLED once everything is ticked rather than removed, and
          the removal is disabled with nothing chosen: a button that disappears
          changes how the rest wrap, and a bar that re-wraps is the shift coming
          back through the side door.

          ONLY WHERE THERE ARE ROWS, or rows on their way. Above "No wants yet"
          it would be chrome for a list that does not exist; held through the
          skeleton as well, so the toolbar does not arrive with the wants and
          push them down the moment they land.

          Under the header rather than at the top of the window: `.cbm-head` is
          sticky and 57px tall, and two sticky things claiming the same line
          means one of them is behind the other.

          "The {n} shown" says what it ticks. An "all my wants" the server
          resolves is what the Selling tab's price control does, and it is a
          different promise: with pagination, "all" can mean rows nobody has
          seen, and this action deletes. That scope is still an open question,
          so this offers only what is on the screen.
        */}
        {wants.length || wantsState === 'loading' ? (
          /* The shared bar (chrome.jsx), the same one Selling and the shop's Stock use. */
          <BulkBar sticky idle={!chosen.length} count={chosen.length} data-cbm="wants-actions">
            <Button
              variant="outline"
              disabled={!wants.length || chosen.length === wants.length}
              onClick={selectAll}
            >
              {t("Select all {n}", { n: wants.length })}
            </Button>
            <Button variant="outline" disabled={!chosen.length} onClick={clear}>
              {t("None")}
            </Button>
            {/* At the far end, for the reason the single row's "Take it down" is:
                a hand reaching for "Clear" should not find the removal. */}
            <Button
              variant="outline" className="cbm-bulk__far"
              disabled={removing || !chosen.length} onClick={removeChosen}
            >
              {removing ? t("One moment…") : t("Remove {n}", { n: chosen.length })}
            </Button>
          </BulkBar>
        ) : null}
        {wants.length ? wants.map((w) => {
          /*
           * The server's count, not a second opinion.
           *
           * This used to re-implement matching here as `title === title`, which
           * disagreed with the server the moment the rule stopped being string
           * equality — and the client cannot do the real rule anyway, because it
           * has no metacard ids. Two different answers to "does this want match?"
           * is worse than one answer computed where the data is.
           *
           * Finding the listing to open still happens here, and it is allowed to
           * be looser than the count: worst case the button is missing on a want
           * the count says has matches, and browsing finds it.
           */
          const count = w.matchesWithinRadius ?? 0;
          /*
           * By the PRINTING first, then by either name. A listing's title
           * reads in the card's own language (2026-10-22), so a Spanish copy
           * arrives as "Elfos de Llanowar" while the want is stored as
           * "Llanowar Elves" — title against title found nothing, and the
           * button fell back to a browse search.
           */
          const names = [w.title, w.localTitle].filter(Boolean).map((n) => n.toLowerCase());
          const opener = listings.find((l) => (l.km ?? 0) <= radiusKm
            && ((w.sourceProductId != null && l.sourceProductId === w.sourceProductId)
              || names.includes(l.title.toLowerCase())));
          /*
           * `cbm-want-row` is for the tests. `loading.spec.ts` measures these
           * against the placeholders drawn in their shape, and it has to be
           * able to say "the rows" now that the toolbar's reserved space is a
           * sibling of them rather than a thing that comes and goes.
           */
          return (
            <div
              key={w.id} className="cbm-want-row"
              data-picked={picked.has(w.id) ? '1' : undefined}
              /* The gesture is read here, before anything inside sees it — see
                 `useSelection`. With a selection live, the whole row answers to
                 it; with none, only the tick box does, and the row's buttons go
                 on being buttons either way. */
              onClickCapture={(e) => press(w.id, e)}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', padding: 'var(--space-5) 0', borderBottom: '1px solid var(--border-hairline)', cursor: selecting ? 'pointer' : undefined, background: picked.has(w.id) ? 'var(--kraft-100)' : undefined }}>
              {/*
                THE TICK BOX STANDS WHERE THE BOOKMARK ICON STOOD, AND THE
                CARD'S NAME IS WHAT NAMES IT.

                An 18px control in the place of an 18px decoration, with the
                row's own text inside the label rather than a second copy of it
                — which is what makes this free twice over.

                GEOMETRY. The row keeps its shape: same leading 18px, same gap,
                same column for the title, so the placeholder drawn in its shape
                while the list loads goes on telling the truth. Losing that is
                what cost this feature its first attempt. The padding grows the
                tap target to 34px and the matching negative margin gives the
                space back, so nothing in the row moves (app.css).

                THE NAME. A screen reader needs to know WHICH want it is about
                to tick, and the design system's Checkbox can only draw a label
                it renders as visible words — so the name here comes from the
                label wrapping the row's existing text. The alternative, a
                clipped `Select <card>` beside it, put every card's title in the
                document twice: a text locator then finds two of everything, and
                the honest count of wants on screen reads double.

                An `aria-label` cannot do this job: what Checkbox spreads lands
                on its own label element, where ARIA does not reach the input.

                The icon this replaces said "this is a want" on a list of
                nothing but wants, next to the word "watching" saying it again.
              */}
              <label className="cbm-pick">
                {/* Drawn from the selection, never from its own event: the
                    press above owns the gesture, so this is told what it is. */}
                <Checkbox checked={picked.has(w.id)} onChange={() => {}} />
                <span className="cbm-pick__what">
                  {/* In the WANT's language where we hold that name, English
                      otherwise — never the interface's (the owner, 2026-10-23).
                      `w.title` stays the English one everything else keys on. */}
                  <span style={{ display: 'block', font: 'var(--type-ui)', fontSize: 'var(--text-base)', color: 'var(--text-strong)' }}>{w.localTitle || w.title}</span>
                  {/*
                    WHAT THIS WANT WILL ACCEPT, said on the row — migration 137.
                    Two wants for one card differing only in their language look
                    identical without this, and the one that stopped notifying
                    looks broken rather than narrow. Only what was answered is
                    shown: "any" is the absence of a word, not the word "any",
                    because most wants are any on most counts and a row reading
                    "Any condition · Any language · Foil or not" says nothing
                    four times.
                  */}
                  <span style={{ display: 'block', font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                    {[
                      gameLabel(w.game),
                      // "or better", because the condition is a floor. Without
                      // those two words the row claims the opposite of the rule.
                      w.minGrade ? t("{grade} or better", { grade: w.minGrade }) : null,
                      w.language ? t(CARD_LANGUAGE_NAMES[w.language] || w.language) : null,
                      w.foil === true ? t("Foil only") : w.foil === false ? t("Non-foil only") : null,
                      w.exactPrinting ? t("this printing only") : null,
                      w.note || null,
                    ].filter(Boolean).join(" · ")}
                  </span>
                </span>
              </label>
              {count
                ? (opener
                  ? <Button size="sm" variant="secondary" onClick={() => go('listing', { id: opener.id })}>{t("{count} nearby", { count })}</Button>
                  : <Button size="sm" variant="secondary" onClick={() => go('browse', { q: w.title })}>{t("{count} nearby", { count })}</Button>)
                : <span style={{ font: 'var(--type-meta)', color: 'var(--text-faint)' }}>{t("watching")}</span>}
              <IconButton icon="x" label={t("Remove {card}", { card: w.localTitle || w.title })} variant="ghost" size="sm" onClick={() => removeOne(w)} />
            </div>
          );
        })
          /*
           * "No wants yet" was the third of these said before anybody had asked,
           * and it is the one that arrives with an invitation: the form to add a
           * want sits directly beside it, so the false answer comes with
           * something to act on.
           *
           * The list has to be empty as well as loading, so a refetch over a list
           * somebody is reading never replaces it with grey rows.
           */
          : wantsState === 'error' ? (
            <EmptyState icon="alert-triangle" title={t("We could not load your wants")}
              description={t("Something went wrong at our end, not yours. They are still being watched for.")} />
          ) : wantsState === 'loading' ? (
            <WantRowsSkeleton label={t("Loading your wants.")} />
          ) : <EmptyState icon="bookmark" title={t("No wants yet")} description={t("Name a card and we'll watch every listing inside your radius for it.")} />}
        {/*
          * THE PAGE AFTER THE FIRST, WHICH THIS SCREEN NEVER OFFERED.
          *
          * The server has paged this endpoint all along — `/api/wants` takes a
          * limit and a cursor and returns `nextCursor` — and the store has
          * carried `cursors.wants` and known how to fetch the next page for just
          * as long. This component simply never asked for either, so somebody
          * with more than fifty wants had the rest of them silently out of
          * reach: not an error, not an empty state, just a list that stopped.
          *
          * Reported as "there's no pagination on the wants page", which was true
          * of the page and of nothing underneath it.
          */}
        <LoadMore
          cursor={cursors.wants}
          busy={loadingMore === 'wants'}
          onClick={() => loadMore('wants')}
        />
      </div>
      {/* The house handle. "Card" names both this field and the search box over
          the list beside it, which is fine for a reader — they are in different
          places — and ambiguous to anything selecting by name. */}
      <Card tone="sunken" padding="var(--space-5)" data-cbm="add-want"
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {/*
          A HEADING, NOT A FIELD CAPTION.

          Reported: "the 'Add a want' title for the whole form has exactly the
          same style as the close 'Card' label for the inputs." It was
          `cbm-label` — the uppercase, muted, letter-spaced style the design
          system uses for the caption ON a field — so the form's own name read
          as one more label in a stack of them, and the first real field looked
          like it belonged to whatever was above it.

          `cbm-h2` at `--text-lg`, the size `referral.jsx` uses for a heading
          inside a card: an h2 at its full size would outweigh the page title
          two cards up. And an <h2> rather than a styled span, because it IS the
          heading of this region and a screen reader should be able to jump to
          it.
        */}
        <h2 className="cbm-h2" style={{ fontSize: 'var(--text-lg)' }}>{t("Add a want")}</h2>
        {/* The same typeahead the posting form uses. Naming a want is the same
            act as naming a listing, and getting the name slightly wrong here is
            worse: a want that never matches sits there watching for a card that
            does not exist under that spelling. Picking a suggestion sets the game
            too, which is the field people leave on whatever it opened at. */}
        <CardAutocomplete
          label={t("Card")}
          value={title}
          onChange={(v) => { setTitle(v); setProductId(null); setExactPrinting(false); }}
          onPick={(item) => { setTitle(suggestionNameFor(item)); setPickedName(item.name); setProductId(item.productId || null); if (item.game) setGame(item.game); }}
          onEnter={submit}
        />
        <Select label={t("Game")} options={GAMES.map((g) => ({ value: g.id, label: g.label }))} value={game} onChange={setGame} />
        <WantConditions
          minGrade={minGrade} setMinGrade={setMinGrade}
          language={language} setLanguage={setLanguage}
          foil={foil} setFoil={setFoil}
          productId={productId}
          exactPrinting={exactPrinting} setExactPrinting={setExactPrinting}
        />
        <Button fullWidth onClick={submit} disabled={!title.trim()}>{t("Add a want")}</Button>

        {/* Confirming a want carried over from a search. Everything is already
            filled in; this exists so the person sees what is about to be saved
            rather than discovering it afterwards. */}
        <ModalHost>
          {confirming ? (
            <Dialog open closeLabel={t("Close")} width={420}
              title={t("Watch for this card?")}
              description={t("We’ll tell you when one appears inside your radius. You can change the game or add a note first.")}
              onClose={() => { setConfirming(false); clearHandover(); }}
              footer={<>
                <Button variant="ghost" onClick={() => { setConfirming(false); clearHandover(); }}>{t("Cancel")}</Button>
                <Button onClick={() => { setConfirming(false); submit(); }}>{t("Add a want")}</Button>
              </>}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <div style={{ font: 'var(--type-ui)', fontSize: 'var(--text-base)', color: 'var(--text-strong)' }}>{title}</div>
                <Select label={t("Game")} options={GAMES.map((g) => ({ value: g.id, label: g.label }))} value={game} onChange={setGame} />
                {/*
                  THE SAME CONTROL THE SIDEBAR DRAWS, not a second copy of it.
                  The copy that used to be here was still writing into `note`
                  after the sidebar's was rewired to `min_grade` — two forms
                  asking one question is two answers waiting to disagree.
                */}
                <WantConditions
                  minGrade={minGrade} setMinGrade={setMinGrade}
                  language={language} setLanguage={setLanguage}
                  foil={foil} setFoil={setFoil}
                  productId={productId}
                  exactPrinting={exactPrinting} setExactPrinting={setExactPrinting}
                />
              </div>
            </Dialog>
          ) : null}
        </ModalHost>
        {confirmDialog}
        <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{t("We only alert you about listings you could reach — no shipping offers.")}</span>
      </Card>
    </div>
  );
}

/**
 * The five things worth telling somebody about.
 *
 * Held as data rather than written out per channel, because they ARE the same
 * three questions on both: two hand-written lists asking "a card I want turns
 * up nearby" twice is two places for the wording to drift, and a channel added
 * later would make it three.
 *
 * The English sentence is the key, so these are the strings `t()` receives.
 */
const NOTIFICATION_KINDS = [
  {
    id: 'want_match',
    label: "A card I want turns up nearby",
    description: "Somebody posts a card on your wants list, inside your radius",
    emailKey: 'notifyWantMatch',
    pushKey: 'pushWantMatch',
  },
  {
    id: 'messages',
    label: "Somebody writes to me",
    description: "A message you have not read",
    emailKey: 'notifyMessages',
    pushKey: 'pushMessages',
  },
  {
    id: 'trades',
    label: "Somebody is waiting on my answer",
    description: "A trade proposed to you and still unanswered",
    emailKey: 'notifyTrades',
    pushKey: 'pushTrades',
  },
  {
    id: 'draw_result',
    label: "A draw I entered has been drawn",
    description: "The winners are out — it never says whether you won",
    emailKey: 'notifyDrawResult',
    pushKey: 'pushDrawResult',
  },
  {
    /*
     * THE ONLY ONE HERE NOBODY ASKED FOR, and it is last on purpose. Every row
     * above is news about this person — their card, their message, their draw.
     * This one is us telling them about our own promotion, and a switch for it
     * has to be as easy to find as the others precisely because of that.
     */
    id: 'new_campaign',
    label: "A new draw opens",
    description: "We are running a draw and you can enter by inviting somebody",
    emailKey: 'notifyNewCampaign',
    pushKey: 'pushNewCampaign',
  },
];

/**
 * A shop of your own, from the profile.
 *
 * WHY THIS EXISTS AT ALL: the whole flow was on the server and reachable from
 * nowhere -- apply, verify, reject with a note somebody can answer, a nearby
 * lookup -- and production held zero shops. Not because nobody wanted one;
 * because no screen ever asked.
 *
 * WHAT A SHOP IS FOR, since it is not a seller badge: a verified shop is a
 * PLACE A TRADE CAN BE ARRANGED AT, and since migration 128 it is also an
 * ACCOUNT -- its own stock, its own trades, its own name on them. The applying
 * and the shop's own screens live in `shop.jsx` and `shop-dash.jsx`; what is
 * left here is the way in.
 */
/**
 * THE SHOP, AS A CONTROL RATHER THAN A CARD.
 *
 * This was a card on the profile — name, badge, address, and a note about the
 * badge — and the owner read the design back to me: "when we have a verified
 * shop then instead of showing a whole big card about the shop in the profile,
 * a Shop button is shown in black somewhere to go to the dashboard, right?"
 * Right, and `ui_kits/app/you.jsx` in Claude Design says it in code: a
 * `secondary` button carrying the shop's name, in the row with Invite friends
 * and Post a card. `secondary` IS the black one — `--ink-900`, which is why he
 * remembered it as black.
 *
 * And the card was saying things this screen is the wrong place for. The
 * address, the badge and the warning about re-checking belong to the shop's own
 * screens, which exist now; on a person's profile they were a second copy that
 * could disagree with them. What belongs here is the door.
 *
 * THE STATE STILL HAS TO SHOW, which is why this is not simply one button. An
 * application nobody has looked at yet is the one thing somebody checks their
 * profile FOR, and a shop that was turned down has to be able to find its way
 * back to the form. Those get a quieter control each, and the sentence behind
 * them lives on `/shop`.
 */
function ShopEntry({ shop }) {
  const { trader, actAs, go, t } = useApp();
  if (!shop) return null;

  /*
   * REJECTED GOES TO THE FORM, not to the back office. It is the one state
   * whose next step is typing: an address an operator could not find, and a
   * note explaining which. Everything else goes to the shop itself.
   */
  if (shop.status === 'rejected') {
    return (
      <Button variant="outline" iconLeft="alert-triangle" onClick={() => go('shop')}>
        {t("Your store needs a fix")}
      </Button>
    );
  }

  /*
   * AND WAITING GOES TO THE BACK OFFICE TOO, which is not what the design
   * drew: its pending state is a ghost button back to the application.
   *
   * Because the shop can already WORK. The account, the membership and the
   * stock all exist from the moment the form is sent — verification buys the
   * badge and the pin on the map, nothing else — and the waiting screen tells
   * a shopkeeper to get their stock ready. A door that leads back to the form
   * they have already filled in is a door that says "wait two days", which is
   * how a shop that signed up on Monday opens on Thursday.
   *
   * The clock is the difference, and the state itself is said on the dashboard.
   */
  const waiting = shop.status !== 'verified';
  return (
    <Button
      variant={waiting ? 'outline' : 'secondary'}
      iconLeft={waiting ? 'clock' : 'store'}
      /*
       * STRAIGHT INTO THE SHOP. Pressing this is the whole gesture — nobody
       * presses their own shop's name to be asked whether they meant it — so it
       * puts the session on the shop and lands on Today in one go. Already
       * being the shop, it is just the way back to Today.
       */
      onClick={() => (trader && trader.kind === 'shop' ? go('dashboard') : actAs(shop.id, '/shop/dashboard'))}
    >
      {shop.name}
    </Button>
  );
}

/**
 * QUIET UNTIL THERE IS SOMETHING TO SAY.
 *
 * On the profile rather than in Settings, which means almost everybody who sees
 * it has no shop and never will. A card inviting them to open one, every visit,
 * would be an advertisement on their own page — so with no shop this is one
 * line, and with one it is nothing at all, because the button in the header is
 * already the answer.
 */
function ShopInvite({ shop }) {
  const { go, t } = useApp();
  if (shop) return null;
  return (
    <p style={{ margin: 'var(--space-6) 0 0', font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>
      {t("Do you run a card shop?")}{' '}
      <button type="button" className="cbm-textlink" onClick={() => go('shop')}>
        {t("Open a store account")}
      </button>
    </p>
  );
}

function SettingsPanel() {
  const { settings, setSettings, radius, setRadius, point, setPoint, activeLocation, games, setGames, languages, setLanguages, profile, setNotifyPreference, saveContact, replayOnboarding, logout, notify, trader, go, locale, t } = useApp();
  const [support, setSupport] = React.useState(false);
  const [confirm, confirmDialog] = useConfirm();
  /* The two controls under "Your data". `erasing` opens the confirmation;
     `erasingNow` is the request in flight, and is what stops somebody pressing
     it twice or closing the dialog out from under it. */
  const [exporting, setExporting] = React.useState(false);
  const [erasing, setErasing] = React.useState(false);
  const [erasingNow, setErasingNow] = React.useState(false);

  /*
   * The email preferences live on the ACCOUNT, not in this component.
   *
   * They have to: an email is sent by a server at four in the morning to a person
   * who is not looking at any device, so the answer cannot be a value in one
   * browser's localStorage. `settings.alerts` above IS per device, correctly —
   * whether Browse shows a banner is about the screen in front of you.
   *
   * Optimistic, and put back on failure, because a toggle that waits for a round
   * trip before it moves reads as broken.
   */
  const [saving, setSaving] = React.useState(null);

  /*
   * The two contact fields, held locally while being typed.
   *
   * Not saved on every keystroke: a half-typed phone number is not a phone
   * number, and validating one on the server as somebody types it produces a
   * form that argues with them mid-word.
   */
  const [contacts, setContacts] = React.useState({ whatsapp: '', telegram: '', cardmarketUsername: '' });
  const [contactBusy, setContactBusy] = React.useState(null);

  /*
   * Seeded ONCE, not kept in sync with the profile.
   *
   * Syncing was the obvious version and it ate what people typed. Saving one
   * field replaces the whole trader, which re-ran the effect, which reset BOTH
   * fields from the account — so typing a Telegram username, tabbing to
   * WhatsApp and typing a number lost the number the moment the first field
   * saved on blur. The same shape as the poll that used to throw away the page
   * you had just loaded: a background update overwriting a foreground edit.
   *
   * So this fills the form when the account first arrives, and after that each
   * field is only ever written by the person typing in it or by its own save
   * returning a normalised value.
   */
  const seeded = React.useRef(false);
  React.useEffect(() => {
    if (seeded.current || !profile) return;
    seeded.current = true;
    setContacts({
      // Shown back in the readable form, so the full number is visible before
      // anybody agrees to publish it.
      whatsapp: profile.whatsapp ? cbmFormatWhatsApp(profile.whatsapp) : '',
      telegram: profile.telegram ? '@' + profile.telegram : '',
      // Plain, with nothing in front of it: it is the name in the address of a
      // Cardmarket page, not a handle anybody writes with a sigil.
      cardmarketUsername: profile.cardmarketUsername || '',
    });
  }, [profile]);

  const persistContact = async (channel, raw) => {
    setContactBusy(channel);
    try {
      const updated = await saveContact(channel, raw);
      /*
       * Only this field, and only from its own response. Reading the whole
       * account back here would clobber whatever is half-typed in the other one.
       */
      const stored = updated.trader && updated.trader[channel];
      setContacts((current) => ({
        ...current,
        [channel]: !stored
          ? ''
          : channel === 'whatsapp' ? cbmFormatWhatsApp(stored)
          // No '@' on a Cardmarket name. The two above are handles people write
          // with one; this is a path segment, and showing it with a sigil it
          // does not have invites somebody to type the sigil back in.
          : channel === 'cardmarketUsername' ? stored
          : '@' + stored,
      }));
    } catch (e) {
      /*
       * THE CODE, NOT THE MESSAGE — and this was a real bug, not a tidy-up.
       *
       * `ApiError` sets its message to `table[code] || detail || table.unknown`,
       * so `e.message` is never the code: it is either a translated sentence or
       * "Something went wrong. Try again." Every branch below was written
       * against `e.message.includes('whatsapp_invalid')` and NONE of them had
       * ever fired. Somebody typing a bad number was told "That could not be
       * saved" — the last resort — and never the sentence written for them.
       *
       * The same shape as the fallback that hid a dead feature: a catch whose
       * every branch is unreachable still behaves, so nothing looks broken.
       */
      const code = (e && e.code) || String((e && e.message) || e);
      notify(
        /*
         * The missing-country-code case has its own sentence, because telling
         * somebody who typed their own number correctly that it "does not look
         * like a phone number" is an argument the form should not be having.
         * The server stopped guessing `+34` for a bare nine digits — a French
         * and an Italian mobile are nine digits too, and the guess messaged
         * whoever actually held the Spanish one.
         */
        code === 'whatsapp_needs_country'
          ? t("Add your country code, like +34 600 11 22 33 or +39 333 123 4567.")
          : code === 'whatsapp_invalid'
          ? t("That does not look like a phone number. Include the country code, like +34 600 11 22 33.")
          : code === 'telegram_invalid'
            ? t("That does not look like a Telegram username. They are five characters or more, letters, numbers and underscores.")
          /*
           * The Cardmarket name is refused by the SCHEMA rather than by a
           * hand-written check, so there is no code of its own to branch on —
           * the shape is in the pattern and a bad one is a 400. Named here by
           * the field being saved instead, because "that could not be saved"
           * for a username that is simply too short is the kind of dead end
           * `ApiError` taught this form to avoid.
           */
          : channel === 'cardmarketUsername'
            ? t("That does not look like a Cardmarket username. Three characters or more, letters, numbers, hyphens and underscores.")
            : t("That could not be saved."),
        'caution',
      );
    } finally {
      setContactBusy(null);
    }
  };

  /*
   * This device's push state, asked rather than remembered.
   *
   * Permission can be revoked from the browser's own settings and a
   * subscription can be dropped by the push service, and neither tells us. A
   * value kept in localStorage would therefore be a switch showing the wrong
   * position, which is worse than no switch at all.
   */
  const [push, setPush] = React.useState({ supported: false, permission: 'default', subscribed: false });
  const [pushBusy, setPushBusy] = React.useState(false);
  /*
   * WHAT THE LAST TEST SAID, and which of the two tests it was.
   *
   * REPORTED, on a phone: notifications enabled, message sent from another
   * account, nothing arrives, and the switch reads "off" again on the next
   * visit. The server side of that is already proved innocent — the outbox
   * shows the push accepted by Google for that exact phone — so the failure is
   * in the gap between Google and the screen, which nothing here can see.
   *
   * Two buttons split that gap. One shows a notification from this page with no
   * push involved: if THAT does not appear, Android is refusing to display
   * anything and no message we send will ever be seen. The other sends a real
   * push through the queue's own code and reports what the push service said.
   * Together they turn "it does not work" into one of three specific answers.
   */
  const [probe, setProbe] = React.useState(null);
  const [worker, setWorker] = React.useState(null);
  const [resending, setResending] = React.useState(false);
  /*
   * WHAT THE SERVER CAN ACTUALLY REACH, which is not what the switch knows.
   *
   * The switch reads the BROWSER: it is on when this browser holds a
   * subscription. The server is the other half, and the two drift — a push
   * service retires an endpoint, we delete the row because it will never work
   * again, and the browser goes on handing back a subscription object. The
   * switch then says "on" for a device nothing can reach, which is the shape of
   * the report: "they were not working... while B had them enabled".
   *
   * So the devices the SERVER holds are shown underneath, with when each was
   * last reached. Nobody has to believe a switch.
   */
  const [devices, setDevices] = React.useState(null);
  const refreshPush = React.useCallback(async () => {
    setPush(await currentState());
    try { setDevices((await api.pushDevices()).devices || []); } catch { setDevices(null); }
  }, []);
  React.useEffect(() => { void refreshPush(); }, [refreshPush]);
  /*
   * THE DIAGNOSTICS, FOR OPERATORS ONLY.
   *
   * These were built in front of everybody while push was being chased across
   * three installs on one phone, and they earned their keep: two rounds of
   * fixes came from a reading somebody screenshotted. That was the debugging
   * phase and it is over.
   *
   * What is left for an ordinary trader is a switch that says whether
   * notifications are on and a list of the devices we can reach — the two
   * things a person can act on. `page=default browser=granted sub=present` is
   * not one of them; it is a sentence written to me, and on somebody else's
   * screen it reads as a fault they cannot fix.
   *
   * HIDING IS A COURTESY, NOT A PROTECTION, like the back-office button below:
   * `pushTest` is an ordinary authenticated endpoint and stays that way. What
   * changes is who is invited to press it.
   */
  const operator = !!trader?.isAdmin;
  // Which worker is actually running here, asked of the worker. An old one with
  // no push handler is a silent phone, and it looks exactly like a broken server.
  // Not asked at all where it will not be shown.
  React.useEffect(() => { if (operator) void workerVersion().then(setWorker); }, [operator]);
  /*
   * THE RAW READINGS, because two rounds of fixes came from inferring this
   * device's state out of screenshots. A live subscription beside an origin
   * with no notification permission is a state none of our code can produce,
   * and the browser can simply be asked.
   */
  const [readings, setReadings] = React.useState(null);
  React.useEffect(() => { if (operator) void pushDiagnosis().then(setReadings); }, [push.subscribed, operator]);

  const togglePush = async (on) => {
    setPushBusy(true);
    try {
      if (on) await enable();
      else await disable();
    } catch (e) {
      /*
       * "denied" is not a failure, it is an answer — and the one thing we must
       * not do is ask again. A browser told never gives no second prompt, so
       * the honest thing is to say where the answer now lives.
       */
      const reason = String((e && e.message) || e);
      notify(
        reason === 'denied'
          ? t("Your browser is blocking notifications for this site. You can allow them in its settings.")
          : reason === 'unavailable'
            ? t("Push notifications are not set up on this server yet.")
            : reason === 'default'
              // Dismissed rather than answered. Saying so matters: nothing is
              // broken and pressing it again will ask again, which is not true
              // of a refusal.
              ? t("The permission request was closed without an answer. Try again.")
              : t("Notifications could not be turned on: {reason}", { reason }),
        'caution',
      );
      // The real reason, for a person who can open a console and tell us. A
      // toggle that fails silently is one nobody can report usefully.
      console.error('push could not be enabled', e);
    } finally {
      setPushBusy(false);
      await refreshPush();
    }
  };

  const resendVerification = async () => {
    setResending(true);
    try {
      const r = await api.resendVerification();
      // The server says whether the mail actually left. Reporting "sent" when it
      // did not is a lie told to the one person who could act on the truth.
      if (r && r.emailSent === false) notify(t("We could not send it just now. Try again in a few minutes."), 'caution');
      else notify(t("Sent. Check your inbox — and your spam folder."));
    } catch (e) {
      notify(e.message, 'caution');
    } finally { setResending(false); }
  };

  /*
   * One cell of the notifications table.
   *
   * The preferences live on the ACCOUNT, not in this component. They have to: an
   * email is sent by a server to somebody who is not looking at any device, so
   * the answer cannot be a value in one browser's localStorage.
   *
   * Optimistic, and put back on failure, because a switch that waits for a round
   * trip before it moves reads as broken.
   */
  const channelSwitch = (key) => (
    <Switch
      checked={profile[key] !== false}
      disabled={saving === key}
      onChange={async (value) => {
        setSaving(key);
        try {
          await setNotifyPreference(key, value);
        } catch (e) {
          notify(t("That setting could not be saved."));
        } finally { setSaving(null); }
      }}
    />
  );

  /**
   * One contact: its field, then its switch on a line of its own.
   *
   * Built as a helper because the two are identical apart from their words, and
   * because the switch row wants the same shape as the notification rows further
   * down — label and explanation on the left, control on the right, aligned to
   * each other rather than to whatever the field above happens to be doing.
   */
  const contactField = ({ channel, label, placeholder, hint, saved, visible }) => (
    <div key={channel} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <Input
        label={label}
        value={contacts[channel]}
        placeholder={placeholder}
        disabled={contactBusy === channel}
        onChange={(e) => setContacts({ ...contacts, [channel]: e.target.value })}
        onBlur={() => persistContact(channel, contacts[channel])}
        hint={hint}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', padding: 'var(--space-3) 0 0' }}>
        <div style={{ flex: 1, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: saved ? 'var(--text-strong)' : 'var(--text-muted)' }}>
          {saved
            ? t("Show this on my listings")
            /* Explained rather than merely disabled: a control that cannot be
               pressed and does not say why is a dead end. */
            : t("Add one above to be able to show it")}
        </div>
        <Switch
          checked={visible}
          disabled={!saved}
          onChange={(v) => setNotifyPreference(channel === 'whatsapp' ? 'whatsappVisible' : 'telegramVisible', v)}
        />
      </div>
    </div>
  );

  const row = (label, description, key) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-5)', padding: 'var(--space-5) 0', borderBottom: '1px solid var(--border-hairline)' }}>
      <div style={{ flex: 1 }}>
        <div style={{ font: 'var(--type-ui)', fontSize: 'var(--text-base)', color: 'var(--text-strong)' }}>{label}</div>
        <div style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{description}</div>
      </div>
      <Switch checked={settings[key]} onChange={(v) => setSettings({ ...settings, [key]: v })} />
    </div>
  );
  return (
    <div style={{ maxWidth: 'var(--container-prose)', display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div>
        {row(t("Tell me when a want turns up"), t("Shows a note on Browse when something on your wants list is inside your radius"), 'alerts')}
        {/*
          The chime has existed since the inbox did and had no control.
          A sound a person cannot turn off is a bug however good the sound is —
          and this one only plays in a background tab, which is precisely where
          an unexpected noise is least welcome and hardest to trace.
        */}
        {row(t("Make a sound for new messages"), t("A short chime when something arrives while you are looking at another tab"), 'sound')}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
        <span className="cbm-label">{t("Language")}</span>
        <LanguageSwitch />
      </div>

      <Select label={t("Default radius")} options={RADIUS_OPTIONS} value={radius} onChange={setRadius} hint={t("Median meetup distance here is 1.6 km.")} />

      {/*
        The games you play.
        Answered once during onboarding and then unchangeable — so somebody who
        picks up a new game a year later had nowhere to say so, and their browse
        stayed filtered to what they played when they signed up. The account has
        always stored this and the endpoint has always accepted it; there was
        simply no control.

        "All games" is what an empty selection looks like, which is what every
        account holds until somebody answers — the same chip, and the same
        reasoning, as the card languages below. It is not a stored value and
        there is no such game; picking it clears the rest, and picking any
        specific game clears it.

        It also replaces the sentence that used to sit under these chips saying
        "None chosen". That was the truth stated as an absence, which reads like
        an unfinished setting rather than an answer — and it was only ever there
        because no chip said it.
      */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <span className="cbm-label">{t("Games you play")}</span>
        <GamePicker
          selected={games}
          onToggle={(id) => setGames(games.includes(id) ? games.filter((x) => x !== id) : games.concat([id]))}
          onAll={() => setGames([])} />
        <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {games.length
            ? t("Browse opens on these. You can still search any game.")
            : t("All games, so browse opens on everything.")}
        </span>
      </div>

      {/*
        The card languages you want to see, beside the games and behaving the
        same way — both answer "what should browse open on", on different axes.

        This is a PREFERENCE, never a claim about a card. What language a card is
        printed in is its owner's statement about an object; this is an opinion,
        changeable at any time, and nothing derives one from the other. The one
        exception is written down on the posting form: prefer exactly ONE
        language and the form opens on it, because that is your own stated fact
        about your own collection and narrow enough to act on.

        "All languages" is what an empty selection looks like, which is what
        every account holds until somebody answers — so the control reads
        correctly for a trader who has never been here rather than showing
        seventeen unlit chips and no explanation. It is not a stored value and
        there is no such language; picking it clears the rest, and picking any
        specific language clears it.

        The names are in YOUR language, and the three you are most likely to want
        come before the reveal — but a language you have ALREADY saved is never
        behind it. Somebody who chose Korean a month ago must find Korean lit
        when they come back to change it, not a collapsed row that looks like
        their preference was forgotten.
      */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <span className="cbm-label">{t("Card languages you want to see")}</span>
        {/* The games chosen just above decide which languages can exist. */}
        <CardLanguagePicker
          selected={languages}
          games={games}
          onToggle={(code) => setLanguages(languages.includes(code) ? languages.filter((x) => x !== code) : languages.concat([code]))}
          onAll={() => setLanguages([])} />
        <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {languages.length
            ? t("Browse opens on these. You can still see any language from the filters.")
            : t("All languages, so browse opens on everything.")}
        </span>
      </div>
      <div>
        <div className="cbm-label" style={{ marginBottom: 'var(--space-3)' }}>{t("Your location")}</div>
        <LocationPicker value={point} onChange={setPoint} />
        {/* Confirmation that something IS saved. Not a place name — there are none
            anywhere in this product — and not a bare coordinate either, which
            means nothing to a person. The pin on the map above is the real answer;
            this is the sentence that says the map is showing a saved value rather
            than a default. */}
        {point ? (
          <p className="cbm-meta" style={{ margin: 'var(--space-3) 0 0', color: 'var(--text-muted)' }}>
            {/* Which of the saved locations this map is editing, said out loud.
                A trader can hold several now, and the pin above moves the ACTIVE
                one — the whole list lives in the location dialog. Without the
                name this control silently edits one of five. */}
            {activeLocation
              ? t("Saved as {label}. Distances are measured from the pin above.", { label: activeLocation.label })
              : t("Saved. Distances are measured from the pin above.")}
          </p>
        ) : (
          <p className="cbm-meta" style={{ margin: 'var(--space-3) 0 0', color: 'var(--text-muted)' }}>
            {t("Nothing saved yet, so nothing can be measured from you.")}
          </p>
        )}
        <p className="cbm-meta" style={{ margin: 'var(--space-3) 0 0', color: 'var(--text-muted)' }}>
          {t("Used to work out distances. Never shown to another trader — they see a distance and nothing else.")}
        </p>
      </div>

      {/*
        Notifications, as one table.
        A notification is one thing — somebody should be told — and email and push
        are two ways of saying it. Two separate lists asked the same three
        questions twice and invited them to drift; a row per thing that happens
        and a column per channel says what is actually being chosen. It sits near
        the bottom because it is the setting people change least often.
      */}
      {/*
        Being reachable outside the app.
        People trade on WhatsApp here. A trader with nowhere to put a contact puts
        it in the listing title, where it is unvalidated, permanently public and
        cannot be withdrawn — so this is the safer option rather than the riskier
        one. What makes it safe is that saving and SHOWING are two separate acts.
      */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <span className="cbm-label">{t("How people can reach you")}</span>
        <p style={{ margin: 0, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {t("Optional. Saving a contact does not show it to anybody — that is the switch beside it, and you can turn it off at any time.")}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', paddingTop: 'var(--space-3)' }}>
          {/*
            The switch is its OWN row beneath the field, not a column beside it.

            Beside it, it never lines up: `Input` renders a label above and a hint
            below, so the group's vertical centre is not the input's, and any
            padding that looks right stops being right the moment a hint wraps to
            two lines. It sat next to the hint text rather than the box.

            On its own row it also reads better, because it makes the two-step
            nature explicit — a saved handle and a shown handle are different
            things, and now the screen says so in words rather than relying on
            somebody inferring it from a switch's position.
          */}
          {contactField({
            channel: 'telegram',
            label: t("Telegram username"),
            placeholder: '@nombre',
            hint: t("A username, not a phone number — which is why we suggest this one first."),
            saved: profile && profile.telegram,
            visible: Boolean(profile && profile.telegramVisible),
          })}
          {contactField({
            channel: 'whatsapp',
            label: t("WhatsApp"),
            placeholder: '+34 600 11 22 33',
            hint: t("This is your phone number. Anybody signed in can see it on your listings while the switch is on."),
            saved: profile && profile.whatsapp,
            visible: Boolean(profile && profile.whatsappVisible),
          })}
          {/*
            THE CARDMARKET NAME, AND IT HAS NO SWITCH.

            The two above publish a phone number and a personal messaging
            account, so each is two decisions — store it, and agree to show it.
            This is the address of a public page that already exists and that
            anybody can read without us; adding a switch would be asking for
            consent to publish something already published, and defaulting it
            off would mean a trader types their name, saves it, and nothing
            happens anywhere. A control that does nothing is worse than none.

            So storing it IS the consent, and the hint says so in the sentence
            rather than leaving it to be discovered.

            The owner's correction is why it is on this screen at all: it was a
            shop's column for two days — "a regular trader can have and can
            benefit from adding its cardmarket handle too. It should be allowed
            for both kinds of traders." Migration 134 moved it to the account.
          */}
          {/* No placeholder, for the reason given on the shop form: any handle
              short enough to read as an example is short enough to be
              somebody's, and the hint below already says what goes here. */}
          <Input
            label={t("Cardmarket username")}
            value={contacts.cardmarketUsername}
            disabled={contactBusy === 'cardmarketUsername'}
            onChange={(e) => setContacts({ ...contacts, cardmarketUsername: e.target.value })}
            onBlur={() => persistContact('cardmarketUsername', contacts.cardmarketUsername)}
            hint={t("Your name on Cardmarket, so buyers can see the rest of what you sell. Shown on your listings while it is set.")}
          />
        </div>

        <p style={{ margin: 0, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {t("Signing in is required to see a contact, and the app's own messages still work either way — they are what a trade and a report are built on.")}
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <span className="cbm-label">{t("Notifications")}</span>
        <p style={{ margin: 0, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {t("Sent as soon as something happens, and grouped so a busy afternoon is still one message.")}
        </p>

        {/*
          Said here because nothing else says it.
          An unconfirmed address is never written to — sending to one nobody has
          proved they can read is how a bounce rate is built. But the switches
          look identical either way, so somebody turns them on, receives nothing,
          and reasonably concludes the feature is broken. The banner elsewhere
          only appears where confirmation gates posting, which is not everywhere.
        */}
        {profile && profile.emailVerified === false ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', padding: 'var(--space-4)', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)' }}>
            <div style={{ flex: 1, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-strong)' }}>
              {t("Confirm your email address before we can write to you. Nothing is sent to an address nobody has confirmed.")}
            </div>
            <Button size="sm" variant="ghost" disabled={resending} onClick={resendVerification}>
              {t("Send it again")}
            </Button>
          </div>
        ) : null}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', font: 'var(--type-ui)', fontSize: 'var(--text-sm)' }}>
            <thead>
              <tr>
                <th scope="col" style={{ textAlign: 'left', padding: 'var(--space-3) 0', font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', fontWeight: 400 }}>
                  {t("Tell me when")}
                </th>
                <th scope="col" style={{ padding: 'var(--space-3) var(--space-4)', font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', fontWeight: 400, width: '5.5rem' }}>
                  {t("Email")}
                </th>
                <th scope="col" style={{ padding: 'var(--space-3) 0 var(--space-3) var(--space-4)', font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', fontWeight: 400, width: '5.5rem' }}>
                  {t("Device")}
                </th>
              </tr>
            </thead>
            <tbody>
              {NOTIFICATION_KINDS.map(({ id, label, description, emailKey, pushKey }) => (
                <tr key={id} style={{ borderTop: '1px solid var(--border-hairline)' }}>
                  <th scope="row" style={{ textAlign: 'left', padding: 'var(--space-4) var(--space-4) var(--space-4) 0', fontWeight: 400 }}>
                    <div style={{ font: 'var(--type-ui)', fontSize: 'var(--text-base)', color: 'var(--text-strong)' }}>{t(label)}</div>
                    <div style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{t(description)}</div>
                  </th>
                  <td style={{ padding: 'var(--space-4)', textAlign: 'center' }}>{channelSwitch(emailKey)}</td>
                  <td style={{ padding: 'var(--space-4) 0 var(--space-4) var(--space-4)', textAlign: 'center' }}>
                    {/* Off and unreachable until this device is subscribed, because
                        no column setting can conjure a delivery to a browser that
                        has not granted permission. */}
                    {push.subscribed ? channelSwitch(pushKey) : <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>

            {/*
              The device column's own switch, in the device column.
              It was a flex row under the table with the switch pushed to the
              far right, so it sat past the column it governs — the one control
              that had to line up with the Device switches was the one that did
              not. In the foot it inherits the same column widths, which is what
              makes it align rather than merely look aligned at one width.

              Two levels, and only one of them is ours: whether this browser has
              a subscription at all, and then which things may use it. The
              permission prompt is the real switch and it belongs to the person.
            */}
            {push.supported ? (
              <tfoot>
                <tr style={{ borderTop: '1px solid var(--border-hairline)' }}>
                  <th scope="row" colSpan={2} style={{ textAlign: 'left', padding: 'var(--space-4) var(--space-4) var(--space-4) 0', fontWeight: 400 }}>
                    <div style={{ font: 'var(--type-ui)', fontSize: 'var(--text-base)', color: 'var(--text-strong)' }}>
                      {t("Notify this device")}
                    </div>
                    <div style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                      {push.permission === 'denied'
                        ? t("Blocked by your browser. Allow notifications for this site to turn it on.")
                        : deviceLabel()}
                    </div>
                  </th>
                  <td style={{ padding: 'var(--space-4) 0 var(--space-4) var(--space-4)', textAlign: 'center' }}>
                    <Switch
                      checked={push.subscribed}
                      disabled={pushBusy || push.permission === 'denied'}
                      onChange={togglePush}
                    />
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>

          {/*
            THE SERVER'S OWN ANSWER, and OUTSIDE the switch on purpose.
            
            The switch above reads the BROWSER — it is on when this browser holds
            a subscription — and it only renders where push is supported at all.
            This reads the server, which is the half that can actually reach a
            phone, and it has to be visible from a desktop that supports nothing:
            "which of my devices will be told" is a question about the account,
            not about the browser asking it.
            
            The two drift, and that drift is the reported bug. A push service
            retires an endpoint, we delete the row because it will never work
            again, and the browser goes on handing back a subscription object —
            so the switch says "on" for a device nothing can reach. Nobody should
            have to believe a switch.
          */}
          {/*
            THE TWO PROBES, and they are only offered where they can say
            something: a browser holding a subscription, on the device somebody
            is complaining about.

            Deliberately plain about what each one proves. "Test notification"
            on its own would be one button with three possible meanings, which
            is how a person ends up looking for a fault in the wrong place.
          */}
          {/*
            OFFERED WHERE THERE IS A SUBSCRIPTION, not where the permission
            says so. Inside the Android app the permission belongs to the app
            rather than to the page and can read "default" beside a working
            subscription — which is how these buttons hid on the one device
            somebody needed them on. See lib push.jsx `currentState`.
          */}
          {operator && push.supported && (push.subscribed || push.permission === 'granted') ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', paddingTop: 'var(--space-4)' }}>
              <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    setProbe({ kind: 'local', state: 'sending' });
                    try {
                      await showLocalTestNotification(t("If you can read this, this device can show our notifications."));
                      setProbe({ kind: 'local', state: 'done' });
                    } catch (e) {
                      setProbe({ kind: 'local', state: 'failed', detail: String(e && e.message ? e.message : e) });
                    }
                  }}
                >
                  {t("Show a notification now")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!push.subscribed}
                  onClick={async () => {
                    setProbe({ kind: 'push', state: 'sending' });
                    try {
                      const r = await api.pushTest();
                      setProbe({ kind: 'push', state: 'done', devices: r.devices || [] });
                    } catch (e) {
                      setProbe({ kind: 'push', state: 'failed', detail: String(e && e.message ? e.message : e) });
                    }
                    await refreshPush();
                  }}
                >
                  {t("Send a test notification")}
                </Button>
              </div>
              {/*
                One sentence per outcome, and each names what it has ruled out.
                A result that only said "sent" would leave somebody staring at a
                quiet phone with nothing to go on.
              */}
              {probe ? (
                <p style={{ margin: 0, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {probe.state === 'sending'
                    ? t("Sending…")
                    : probe.kind === 'local'
                      ? probe.state === 'done'
                        ? t("Shown. If nothing appeared, this device is blocking our notifications — check Cardboardmarket in Android's app settings, not here.")
                        : t("This device refused to show it ({detail}). Inside the Android app the permission belongs to the app: turn notifications on for Cardboardmarket in Android's app settings.", { detail: probe.detail || '' })
                      : probe.state === 'failed'
                        ? t("It could not be sent: {detail}", { detail: probe.detail || '' })
                        : probe.devices && probe.devices.length
                          ? probe.devices.map((d) => (
                            d.outcome === 'sent'
                              ? t("{device}: accepted by the push service. If nothing arrived, it was dropped after that.", { device: d.label || t("This device") })
                              : d.outcome === 'gone'
                                ? t("{device}: this device's subscription had expired, so it has been removed. Turn the switch on again to make a new one.", { device: d.label || t("This device") })
                                : t("{device}: it failed — {detail}", { device: d.label || t("This device"), detail: d.detail || '' })
                          )).join(' ')
                          : t("No device is registered to receive notifications.")}
                </p>
              ) : null}
              {/*
                The worker's own version, because the code answering a push on a
                phone is not necessarily the code we shipped — a service worker
                updates on its own schedule and an old one with no push handler
                is a silent phone. Shown only where it can be asked.
              */}
              {worker ? (
                <p style={{ margin: 0, font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
                  {t("Background worker: {version}", { version: worker })}
                </p>
              ) : null}
              {/*
                Untranslated and unpunctuated on purpose: it is a reading, not a
                sentence, and it exists to be screenshotted and sent to me.
                `page` is this page's copy of the permission and `browser` is the
                store `showNotification` actually consults — where those two
                disagree, that disagreement is the whole answer.
              */}
              {readings ? (
                <p style={{ margin: 0, font: 'var(--type-meta)', color: 'var(--text-muted)', wordBreak: 'break-all' }}>
                  {`page=${readings.permission} browser=${readings.queried ?? '—'} sub=${readings.subscription ?? 'none'}`}
                </p>
              ) : null}
            </div>
          ) : null}
          {devices ? (
            <div style={{ font: 'var(--type-meta)', color: 'var(--text-muted)', padding: 'var(--space-4) 0 0' }}>
              {devices.length === 0
                ? t("No device is registered to receive notifications.")
                : devices.map((d) => (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
                    <span>
                      {d.last_ok_at
                        ? t("{device} · last reached {when}", {
                          device: d.label || t("This device"),
                          // The day is enough. A device reached this morning and one
                          // reached three weeks ago are different facts; the minute
                          // is not one.
                          when: new Date(d.last_ok_at).toLocaleDateString(locale),
                        })
                        : t("{device} · never reached yet", { device: d.label || t("This device") })}
                    </span>
                    {/*
                      REMOVING ONE, which the list has needed since it was built.

                      Reported as a chime with nothing to show for it: an evening
                      of a TWA, a PWA and a WebView app left six devices on one
                      account, four of them browsers that had never been reached
                      once. A single message went to the app AND to Chrome on the
                      same phone, so dismissing ours left Chrome's to sound.

                      Nothing here can unsubscribe a browser — that lives in the
                      browser — but taking it off the list is what somebody means
                      by removing a device: we stop being able to reach it.
                    */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        if (!(await confirm({
                          title: t("Remove {device}?", { device: d.label || t("This device") }),
                          description: t("We stop sending it notifications. Switching them on again from that device brings it back."),
                          confirmLabel: t("Remove device"),
                        }))) return;
                        try {
                          await api.pushForgetDevice(d.id);
                          notify(t("Device removed."));
                        } catch (e) {
                          notify(t("That device could not be removed."));
                        }
                        await refreshPush();
                      }}
                    >{t("Remove")}</Button>
                  </div>
                ))}
            </div>
          ) : null}
        </div>

      </div>

      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <Button variant="outline" onClick={() => notify(t("Settings saved."))}>{t("Save changes")}</Button>
        <Button variant="ghost" onClick={replayOnboarding}>{t("Replay the setup")}</Button>
      </div>
      {confirmDialog}

      {/*
        YOUR DATA: THE TWO RIGHTS THE PRIVACY POLICY PROMISES BY NAME.

        It says, in both languages: "Ver y llevarte tus datos: en tu perfil,
        «Exportar»" and "Borrar tu cuenta: en tu perfil". Both endpoints have
        existed since the account surface was built — `GET /api/me/export` and
        `POST /api/me/erase` — and `api.exportMe` and `api.eraseMe` have sat in
        the client beside them. NOTHING CALLED EITHER. There was no control on
        any screen, so a published policy was promising two GDPR rights that a
        trader had no way to exercise, and the Play Data Safety form was about to
        certify the same thing.

        This is the THIRD time that shape has appeared here — the sign-out button
        below carries the same story in its own comment. A method defined in
        api.jsx and called from nowhere looks finished from the API's side and
        does not exist from the product's. `pnpm check:api-unused` now fails on
        it rather than waiting for somebody to go looking.
      */}
      <div style={{ borderTop: '1px solid var(--border-hairline)', paddingTop: 'var(--space-6)', marginTop: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <h3 style={{ font: 'var(--type-ui)', margin: 0 }}>{t("Your data")}</h3>
        <p style={{ font: 'var(--type-caption)', margin: 0, color: 'var(--text-faint)' }}>
          {t("Take a copy of everything we hold about you, or close your account for good.")}
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <Button variant="ghost" iconLeft="download" disabled={exporting} onClick={async () => {
            setExporting(true);
            try {
              /*
               * Fetched and handed over as a file, rather than navigating to the
               * endpoint. A plain navigation relies on the Content-Disposition
               * header being honoured by whatever is rendering us, and this app
               * also runs inside a WebView where a download is something the
               * host has to be listening for. Building the file here means the
               * bytes are already in the page by the time anything has to save
               * them.
               */
              const data = await api.exportMe();
              const href = URL.createObjectURL(
                new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }),
              );
              const a = document.createElement('a');
              a.href = href;
              a.download = 'cardboardmarket-export.json';
              document.body.appendChild(a);
              a.click();
              a.remove();
              // Freed on the next turn: revoking it synchronously can beat the
              // click to the download in some browsers.
              setTimeout(() => URL.revokeObjectURL(href), 10_000);
            } catch (e) {
              notify(t("We could not put your data together. Try again in a moment."));
            } finally {
              setExporting(false);
            }
          }}>{exporting ? t("Putting it together…") : t("Export my data")}</Button>
          {/* Its own colour, because it is the one control on this screen that
              cannot be undone. */}
          <Button variant="ghost" iconLeft="trash-2" onClick={() => setErasing(true)}
            style={{ color: 'var(--color-danger)' }}>{t("Delete my account")}</Button>
        </div>
      </div>

      {/*
        ASKED PROPERLY, BECAUSE IT CANNOT BE TAKEN BACK.

        Not a confirm() and not a single button: the account, the cards, the
        wants and the messages go, and open trades are cancelled on somebody
        else's screen too. The dialog says what goes and what stays — the same
        thing the privacy policy and /eliminar-cuenta say, because a person
        reading three different accounts of it is a person who trusts none.
      */}
      {erasing ? (
        /* The shared confirmation (chrome.jsx), with the account's own words. */
        <ConfirmDialog width={460}
          title={t("Delete your account?")}
          description={t("This cannot be undone.")}
          cancelLabel={t("Keep my account")}
          confirmVariant="danger" busy={erasingNow}
          onCancel={() => setErasing(false)}
          confirmLabel={t("Yes, delete it")} busyLabel={t("Deleting…")}
          onConfirm={async () => {
              setErasingNow(true);
              try {
                await api.eraseMe();
                /*
                 * Straight out, not back to a profile that no longer exists.
                 * `logout` clears the session the server has just invalidated
                 * anyway, and leaves the app on a screen a signed-out visitor is
                 * meant to see.
                 */
                await logout();
              } catch (e) {
                setErasingNow(false);
                notify(t("We could not delete your account. Try again in a moment."));
              }
            /* NOT "Delete my account" again. The control that opened this says
               that, and a confirmation whose button repeats the trigger word for
               word asks somebody to distinguish two identical labels while
               deciding something they cannot undo. This one says what pressing
               it does. */
          }}
        >
          <p style={{ font: 'var(--type-body)', margin: 0 }}>
            {t("Your email, your password, your point on the map, your cards, your wants and your messages are deleted. Photographs go with them, including the ones you took scanning a card. Open trades are cancelled.")}
          </p>
          <p style={{ font: 'var(--type-caption)', margin: 'var(--space-4) 0 0', color: 'var(--text-faint)' }}>
            {t("Trades you already completed are kept without your name on them: they are the record of an agreement, and the other person is entitled to their own copy.")}
          </p>
        </ConfirmDialog>
      ) : null}

      {/*
        Signing out.
        The store has had a `logout` since the app was wired to the API, and the
        API client has had the call. Nothing anywhere invoked either — so the only
        way out of an account was to clear cookies by hand. On a shared or borrowed
        device that is not an inconvenience, it is the account staying open.
      */}
      <div style={{ borderTop: '1px solid var(--border-hairline)', paddingTop: 'var(--space-6)', marginTop: 'var(--space-4)', display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        {/* Reachable here as well as from the footer, because the footer is
            desktop-only and this screen is where somebody goes when something is
            wrong with their account. */}
        <Button variant="ghost" iconLeft="life-buoy" onClick={() => setSupport(true)}>{t("Report a problem")}</Button>
        <Button variant="ghost" iconLeft="log-out" onClick={() => logout()}>{t("Sign out")}</Button>
        {/* Operators only, and hidden rather than disabled: a control somebody
            cannot use is a question they now have. Hiding it is a courtesy and
            not the protection — the API answers 404 to everybody else. Untranslated
            on purpose, like the screen it opens. */}
        {/*
          NAMED FOR THE DOOR, NOT FOR ONE ROOM BEHIND IT.

          REPORTED: "that's the only button/link we have to go to /admin and we
          might want to use it for other things aside from seeing reported
          pictures."

          Quite right, and it had already come true twice over — the screen it
          opens now holds five tabs, of which reported pictures is one. A door
          labelled with whatever was behind it first is a door people stop
          reading, and then the four other things are effectively unreachable
          because nobody expects them there.
        */}
        {trader?.isAdmin ? (
          <Button variant="ghost" iconLeft="layout-dashboard" onClick={() => go('admin')}>
            Go to the back office
          </Button>
        ) : null}
      </div>
      <SupportDialog open={support} onClose={() => setSupport(false)} />
    </div>
  );
}

/**
 * Editing your own listing: the notes, and how many copies you have.
 *
 * Its own component rather than more state inside YouScreen, because the
 * interesting part is a refusal it has to keep on screen. Lowering stock below
 * what accepted trades are holding is refused by the server — correctly, those
 * copies are promised to people who are planning journeys around them — and the
 * old shape for a refusal here was a toast that says "no" and disappears.
 *
 * "No" is not the useful answer. The seller's actual next move is to cancel one
 * of the trades holding a copy, and they cannot do that without knowing which
 * ones those are. So the server sends them and this dialog draws them: how many
 * are committed, across which trades, with whom, and a way to go and deal with
 * them. Then the same change succeeds on the next attempt.
 *
 * A LISTING THE SHOP'S CARDTRADER SYNC BROUGHT IN gets no editor at all — see
 * `SyncedListingDialog`. Both doors (the Selling tab, the shop's Stock row)
 * open this one, so the choice is made once, here.
 */
export function EditListingDialog({ listing, onClose }) {
  return listing.pricing && listing.pricing.managedOnCardtrader
    ? <SyncedListingDialog listing={listing} onClose={onClose} />
    : <ListingEditor listing={listing} onClose={onClose} />;
}

/**
 * A CardTrader-synced listing, AS INFORMATION RATHER THAN FIELDS (write-back
 * spec, stories 23–24; ticket 11).
 *
 * The owner, 2026-10-16: "we should show only read-only information and let
 * them handle modifications from the source synced platform." The sweep writes
 * the price, the copies, the condition, the language and the foil over ours on
 * every pass, and the server refuses every edit to one (`managed_on_cardtrader`)
 * — the note and the photographs included, so there is one rule to learn. So
 * this draws what the listing IS, with one sentence saying where it is changed,
 * and nothing that looks as if it could be typed in. Taking it down is the one
 * thing left to do here, and it stays where it always was, beside this button.
 *
 * Everything on it comes from the listing already held, so it is drawn whole
 * from the first frame and nothing arrives later to move it.
 */
function SyncedListingDialog({ listing, onClose }) {
  const { t } = useApp();
  const grade = listing.condition || listing.grade;
  const stock = listing.stock || { quantity: 1, committed: 0, available: 1 };
  const photos = listing.photos || [];
  const label = { font: 'var(--type-label)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-label)', color: 'var(--text-muted)' };
  const value = { font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)', margin: 0 };
  const row = (name, shown) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <dt style={label}>{name}</dt>
      <dd style={value}>{shown}</dd>
    </div>
  );
  return (
    <Dialog open closeLabel={t("Close")} width={480}
      data-cbm="synced-listing"
      title={t("Listing details")}
      onClose={onClose}
      footer={<Button variant="ghost" onClick={onClose}>{t("Close")}</Button>}
    >
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 'var(--space-5)',
        // The same cap and containment as the editor's body: see there.
        maxHeight: 'calc(100dvh - 260px)', overflowY: 'auto', overscrollBehavior: 'contain',
      }}>
        <Card tone="sunken" padding="var(--space-5)" data-cbm="managed-on-cardtrader"
              style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Icon name="refresh-cw" size={16} />
          <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-body)' }}>
            {t("This listing is managed on CardTrader. Change it there and it updates here within minutes.")}
          </span>
        </Card>
        <dl style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', margin: 0 }}>
          {row(t("Condition"), grade ? `${grade} — ${t(GRADE_NAMES[grade] || grade)}` : '—')}
          {row(t("Language"), `${listing.language ? t(CARD_LANGUAGE_NAMES[listing.language] || listing.language) : '—'}${listing.foil ? ` · ${t("Foil")}` : ''}`)}
          {listing.priceCents != null ? row(t("Price"), api.formatCents(listing.priceCents)) : null}
          {row(t("Copies"), stock.committed > 0
            ? t("{available} of {quantity} free · {committed} promised", { available: stock.available, quantity: stock.quantity, committed: stock.committed })
            : stock.quantity === 1 ? t("One copy") : t("{quantity} copies", { quantity: stock.quantity }))}
          {row(t("Notes for traders"), listing.note || '—')}
          {row(t("Your photos"), photos.length ? (
            <span style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              {photos.map((p) => (
                <img key={p.id} src={p.thumbUrl} alt="" loading="lazy"
                  style={{ width: 84, height: 108, objectFit: 'cover', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-hairline)' }} />
              ))}
            </span>
          ) : t("No photos yet"))}
        </dl>
      </div>
    </Dialog>
  );
}

function ListingEditor({ listing, onClose }) {
  const { editListing, go, actingAsShop, goRequests, t } = useApp();
  const [note, setNote] = React.useState(listing.note || '');
  /*
   * The copies field is a STRING while it is being typed.
   *
   * An empty box is a legitimate intermediate state — somebody clearing "12" to
   * type "2" passes through it — and coercing it to a number the moment it
   * empties would put a 0 or a NaN in the field under their cursor.
   */
  const [copies, setCopies] = React.useState(String((listing.stock || {}).quantity ?? 1));
  /*
   * THE CONDITION, which this dialog used to say outright could not change.
   *
   * Asked for on the shop's stock table — "not anything else like condition or
   * quantity" — and safe for the same reason the price is: a trade keeps its own
   * grade from the moment it is proposed (`trade_item.grade`, read by `itemsFor`
   * without ever joining back to the card), so nothing moves under an agreement
   * somebody already made.
   *
   * A seller who graded a card NM and has since looked at it properly was being
   * told to take the listing down and post it again — which costs them its
   * address and every conversation pointing at it, to be more honest about one
   * field.
   */
  const [grade, setGrade] = React.useState(listing.condition || listing.grade || 'NM');
  /*
   * The price, and only for a listing that HAS one.
   *
   * Editable since migration 074, which fixes the price onto a trade when it is
   * proposed. Before that, changing this would have moved the money under a deal
   * somebody had already agreed to — and nothing would have looked wrong,
   * because every screen read the same live price.
   *
   * A string while it is being typed, for the same reason `copies` is: an empty
   * box is a legitimate step on the way from "12" to "2".
   */
  const [price, setPrice] = React.useState(
    listing.priceCents != null ? api.moneyText(String(listing.priceCents / 100).replace('.', ',')) : '',
  );
  const [busy, setBusy] = React.useState(false);
  /** The server's refusal, kept so its trades can be drawn. See above. */
  const [blocked, setBlocked] = React.useState(null);

  /*
   * FOLLOWING CARDMARKET'S LOW PRICE — migration 091.
   *
   * Offered only where there is something to follow: a listing with a price,
   * pointed at a catalogue product. A card nobody matched to the catalogue has
   * no market price of its own, and a switch that silently does nothing is
   * worse than no switch — so it is absent rather than disabled.
   *
   * `pricing` comes back only to the listing's own seller, which is why it can
   * be read here and is undefined everywhere else.
   *
   * The fields themselves live in chrome.jsx because the posting form asks the
   * same question; what comes back is the parsed setting and whether it is
   * usable, so nothing here has to know a percentage travels as basis points.
   */
  const pricing = listing.pricing || { followsMarket: false, adjust: null, basis: 'low', lowCents: null, avgCents: null };
  const canFollow = listing.priceCents != null && listing.sourceProductId != null;
  const [market, setMarket] = React.useState({
    followsMarket: !!pricing.followsMarket, adjust: pricing.adjust || null,
    basis: pricing.basis || 'low', priceCents: null, valid: true,
  });
  const follows = canFollow && market.followsMarket;

  /*
   * THE PRICE BOX SHOWS WHAT THE RULE PRODUCES, not what the listing used to
   * charge — the same complaint the posting form answered: seeing "You charge
   * 0,04 €" above a box reading 1,50 € is the screen disagreeing with itself.
   *
   * It is read-only while following, so the number on screen is the number that
   * will be saved. A card the catalogue does not price reports null and the box
   * stays exactly as it was, which is also what will happen to it.
   */
  const followedPrice = follows ? market.priceCents : null;
  React.useEffect(() => {
    if (followedPrice == null) return;
    setPrice(api.moneyText(String(followedPrice / 100).replace('.', ',')));
  }, [followedPrice]);

  const stock = listing.stock || { quantity: 1, committed: 0, available: 1 };
  const wanted = parseInt(copies, 10);
  const priceCents = api.centsFromMoney(price);
  const priceBad = listing.priceCents != null && price !== '' && !(priceCents > 0);

  const valid = Number.isInteger(wanted) && wanted >= 1 && !priceBad && market.valid;
  /*
   * The warning BEFORE the button, not only after it.
   *
   * The server has the last word and always will, but a seller typing "1" into
   * a listing with three copies promised should be told what will happen while
   * they are still typing rather than by a rejection afterwards. `committed`
   * comes from the listing itself, which is why the API reports it.
   */
  const willBeRefused = valid && wanted < stock.committed;

  /*
   * Only what actually changed is sent.
   *
   * Not tidiness: the endpoint refuses an empty patch, and sending an unchanged
   * quantity would take the listing's row lock and re-run the committed check
   * for somebody who only fixed a typo in their notes. It also lets the
   * confirmation name what happened rather than saying "listing updated" for
   * every edit.
   */
  const save = async () => {
    if (!valid) return;
    const patch = {};
    if (note !== (listing.note || '')) patch.note = note;
    if (wanted !== stock.quantity) patch.quantity = wanted;
    /*
     * Only where there is a price to change, and only when it actually changed
     * — AND NOT WHILE THIS LISTING FOLLOWS THE MARKET, because a price typed
     * here would be overwritten by the rule in the same request. Sending it
     * would make the reply contradict what the seller just typed.
     */
    if (listing.priceCents != null && !follows && priceCents > 0 && priceCents !== listing.priceCents) {
      patch.priceCents = priceCents;
    }
    if (canFollow) {
      if (follows !== !!pricing.followsMarket) patch.followsMarket = follows;
      /*
       * The adjustment goes only when it really differs, so re-saving a
       * listing whose settings nobody touched does not reprice it.
       *
       * `undefined` and `null` are different answers to the server: absent
       * means "leave it alone", null means "the low price as it is".
       */
      const was = pricing.adjust || null;
      const differs = JSON.stringify(was) !== JSON.stringify(market.adjust);
      if (differs && (follows || market.adjust !== null)) patch.marketAdjust = market.adjust;
      // Sent only when it really changed, so re-saving a listing nobody touched
      // does not reprice it.
      if (market.basis !== (pricing.basis || 'low')) patch.marketBasis = market.basis;
    }
    // Only when it really changed, so re-saving a listing nobody touched does
    // not rewrite the grade with the value it already had.
    if (grade !== (listing.condition || listing.grade)) patch.grade = grade;

    // Nothing to say to the server. Closing is the honest response to "Save"
    // on a form nobody edited.
    if (!Object.keys(patch).length) { onClose(); return; }

    setBusy(true);
    try {
      await editListing(listing.id, patch);
      onClose();
    } catch (e) {
      // The store toasts everything except this one, which is answered on screen
      // instead — with the trades that are actually in the way.
      if (e && e.code === 'stock_below_committed' && e.payload) setBlocked(e.payload);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open closeLabel={t("Close")} width={480}
      /* The house handle for a dialog, which this one never had. `Dialog`
         carries no `role`, so without it a test can only reach the fields by
         name — and the row underneath has a price box of its own. */
      data-cbm="edit-listing"
      title={t("Edit listing")}
      description={listing.priceCents != null
        ? t("The condition, the price, how many copies you have, your notes and the photographs.")
        : t("The condition, how many copies you have, your notes and the photographs.")}
      onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button>
        <Button onClick={save} disabled={!valid || busy}>{busy ? t("One moment…") : t("Save")}</Button>
      </>}
    >
      {/*
        THE BODY SCROLLS, AND THE FOOTER STAYS.

        Found by the browser tests the moment photographs moved in here: the
        dialog grew past the height of a phone, and `Dialog` centres a card with
        `overflow: hidden` and no maximum height — so Save was rendered, enabled,
        and simply OFF THE SCREEN, with nothing to scroll to reach it.
        Playwright's words were "element is outside of the viewport", which is
        also what a seller on a phone would have experienced as "the button does
        nothing".

        Capped here rather than in the design system because the Dialog's own
        body div is not a shrinkable flex item — a max-height on the card alone
        does not make anything inside it scroll. The subtraction leaves room for
        the header and the footer, which live outside this element.
      */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 'var(--space-5)',
        maxHeight: 'calc(100dvh - 260px)', overflowY: 'auto',
        /*
         * AND THE SCROLL STOPS HERE. Without `contain`, reaching the top of
         * this body and pulling further chains the scroll to whatever is behind
         * it — which on Android is what reaches the WebView and fires the
         * native pull-to-refresh, discarding the edit. The page behind is
         * locked while a dialog is open too (see ModalHost); this is the same
         * rule stated at the element that actually scrolls.
         */
        overscrollBehavior: 'contain',
      }}>
        {/*
          THE PRICE, FOR A LISTING THAT HAS ONE.
          
          A listing offered for trade shows no field at all rather than a
          disabled one: there is no price to change, and a greyed box invites
          somebody to wonder what would unlock it.
          
          Changing this moves what the NEXT buyer sees and nothing else. A trade
          fixes its price when it is proposed (migration 074), so an agreement
          somebody already made is not touched — which is the whole reason this
          field can exist at all.
        */}
        <Select
          label={t("Condition")}
          wrapperStyle={{ maxWidth: 240 }}
          value={grade}
          onChange={(v) => setGrade(typeof v === 'string' ? v : v.target.value)}
          hint={t("Trades already agreed keep the condition they were agreed at.")}
          options={GRADES.map((g) => ({ value: g, label: `${g} — ${t(GRADE_NAMES[g] || g)}` }))}
        />
        {listing.priceCents != null ? (
          <Input
            label={t("Price")}
            suffix="€"
            inputMode="decimal"
            wrapperStyle={{ maxWidth: 200 }}
            value={price}
            onChange={(e) => setPrice(api.moneyText(e.target.value))}
            disabled={follows}
            error={priceBad && !follows ? t("Give a price above 0€.") : undefined}
            hint={follows
              ? t("Cardmarket sets this price now. Switch that off below to type your own.")
              : t("Trades already agreed keep the price they were agreed at.")}
          />
        ) : null}

        {/*
          FOLLOWING CARDMARKET, per listing — the seller's own choice, and only
          where there is something to follow.

          ABSENT rather than disabled for a card with no catalogue match: a
          greyed switch invites somebody to hunt for what would unlock it, and
          the answer here is "nothing you can do from this dialog".

          The price above goes read-only while this is on, which is the honest
          shape: a number typed there would be overwritten by the rule before
          the dialog closed.
        */}
        {canFollow ? (
          <MarketFollowFields
            lowCents={pricing.lowCents}
            avgCents={pricing.avgCents}
            initial={{
              followsMarket: !!pricing.followsMarket,
              adjust: pricing.adjust || null,
              basis: pricing.basis || 'low',
            }}
            onChange={setMarket}
          />
        ) : null}

        <Input
          label={t("Copies you have")}
          inputMode="numeric"
          wrapperStyle={{ maxWidth: 160 }}
          value={copies}
          onChange={(e) => { setCopies(e.target.value.replace(/[^0-9]/g, '')); setBlocked(null); }}
          hint={stock.committed > 0
            ? t("{committed} promised to trades you accepted, {available} still free.", { committed: stock.committed, available: stock.available })
            : t("None are promised yet.")}
        />

        {/* Said while they type, so the refusal below is rarely reached. */}
        {willBeRefused && !blocked ? (
          <Card tone="sunken" padding="var(--space-5)" style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Icon name="alert-triangle" size={16} />
            <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-body)' }}>
              {t("You cannot go below {committed} while that many copies are promised to accepted trades. Cancel one of those trades first and the copies come back.", { committed: stock.committed })}
            </span>
          </Card>
        ) : null}

        {/*
          The refusal, with the trades that caused it.
          Each line is one accepted trade and the copies it holds, so the seller
          can recognise which one they are willing to call off — and the button
          takes them there rather than leaving them to find it.
        */}
        {blocked ? (
          <Card tone="sunken" padding="var(--space-5)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <span style={{ font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>
              {t("Not changed. {committed} copies are promised across {count} accepted trades, so you cannot go down to {requested}.",
                { committed: blocked.committed, count: (blocked.trades || []).length, requested: blocked.requested })}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(blocked.trades || []).map((tr) => (
                <div key={tr.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: '8px 10px', background: 'var(--white)', border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-sm)' }}>
                  <span style={{ font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>{tr.them.displayName}</span>
                  {tr.them.handle ? <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>@{tr.them.handle}</span> : null}
                  <span style={{ marginLeft: 'auto', font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
                    {t("{n} copies", { n: tr.quantity })}
                  </span>
                </div>
              ))}
            </div>
            {/* A shop's trades are in its Requests (ticket 08); the accepted
                ones holding copies wait there to be marked done. */}
            <Button size="sm" variant="outline" onClick={() => {
              onClose();
              if (actingAsShop) goRequests();
              else go('trades', { tab: 'active' });
            }}>
              {t("Go to these trades")}
            </Button>
          </Card>
        ) : null}

        <Input
          label={t("Notes for traders")}
          placeholder={t("Sleeved since 2011. Light edge wear on the back.")}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        {/*
          THE PHOTOGRAPHS, in the same dialog as everything else about this
          listing — one intent, one place. They had a button and a dialog of
          their own beside "Edit listing", which made a seller decide which of
          two dialogs their change belonged in before they could make it.

          LAST, and behind a rule of its own, because it does not behave like
          the fields above it: every add and removal saves immediately against
          a listing that already exists, so Cancel does not undo what happens
          down here. The section says so in as many words rather than leaving
          somebody to find out.
        */}
        <FluteDivider />
        <PhotoFields listing={listing} />
      </div>
    </Dialog>
  );
}

/**
 * Market-following across an inventory, which is the only way it scales.
 *
 * ASKED FOR IN THOSE WORDS: "would be nice to be able to change the price sync
 * in bulk from my inventory... A selection of listings or ALL your listings."
 * Somebody who has just imported three hundred cards has three hundred dialogs
 * to open otherwise, and the per-listing switch is right but not enough.
 *
 * ALL IS NOT A SELECTION OF EVERYTHING. Picking "all my listings" sends a flag
 * and lets the server decide what that means as it writes — so a card posted
 * from another tab while this dialog was open is included, and the request does
 * not grow with the inventory. The list below is only for the other case.
 */
/**
 * A following listing's rule, in as few words as it can be said.
 *
 * "Cardmarket · bajo −5%". The basis is the noun and the adjustment is what is
 * done to it, which is the order the dialog asks the questions in — so the mark
 * on the card reads as a summary of the form rather than as a second
 * vocabulary.
 *
 * NO ADJUSTMENT MEANS NO SUFFIX rather than "±0", because "the low price" is
 * already a complete answer and a zero invites somebody to wonder what it is
 * zero of.
 */
function marketRuleText(pricing, t) {
  const basis = pricing.basis === 'avg' ? t("Cardmarket · average") : t("Cardmarket · low");
  const adjust = pricing.adjust;
  if (!adjust) return basis;
  if (adjust.unit === 'basisPoints') {
    const shown = percentTextFromBp(Math.abs(adjust.value));
    return `${basis} ${adjust.value < 0 ? '−' : '+'}${shown}%`;
  }
  const money = api.formatCents(Math.abs(adjust.value));
  return `${basis} ${adjust.value < 0 ? '−' : '+'}${money}`;
}

function BulkFollowDialog({ listings, onClose }) {
  const { setFollowingInBulk, t } = useApp();
  const [scope, setScope] = React.useState('all');
  const [picked, setPicked] = React.useState(() => new Set());
  const [market, setMarket] = React.useState({ followsMarket: true, adjust: null, basis: 'low', priceCents: null, valid: true });
  const [busy, setBusy] = React.useState(false);

  /*
   * A card with no catalogue match is SHOWN and cannot be ticked, rather than
   * hidden. Hiding it would leave a seller counting their listings and finding
   * some missing with no explanation; disabling it with the reason beside it
   * answers the question they were about to ask.
   */
  const eligible = listings.filter((l) => l.priceCents != null);
  const canFollow = (l) => l.sourceProductId != null;
  const chosen = scope === 'all' ? eligible.filter(canFollow) : eligible.filter((l) => picked.has(l.id));
  const ready = market.valid && (scope === 'all' ? chosen.length > 0 : picked.size > 0);

  const toggle = (id) => setPicked((was) => {
    const next = new Set(was);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const apply = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      await setFollowingInBulk({
        ...(scope === 'all' ? { all: true } : { listingIds: [...picked] }),
        followsMarket: market.followsMarket,
        // Sent whenever following is on, null included: null is the first of
        // the three options, not the absence of an answer.
        ...(market.followsMarket ? { marketAdjust: market.adjust, marketBasis: market.basis } : {}),
      });
      onClose();
    } catch {
      // Already said by the store, and said better than a second copy here.
    } finally { setBusy(false); }
  };

  return (
    <Dialog open closeLabel={t("Close")} width={560}
      title={t("Follow Cardmarket's price")}
      description={t("Set this across your listings at once. You can still change any of them on its own afterwards.")}
      onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button>
        <Button onClick={apply} disabled={!ready || busy}>
          {busy ? t("One moment…")
            : scope === 'all' ? t("Apply to all {n} listings", { n: chosen.length })
            : t("Apply to {n} listings", { n: picked.size })}
        </Button>
      </>}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <Select
          label={t("Which listings")}
          value={scope}
          onChange={setScope}
          options={[
            { value: 'all', label: t("All my listings") },
            { value: 'some', label: t("Only the ones I pick") },
          ]}
        />

        {scope === 'some' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
              <Button size="sm" variant="outline"
                onClick={() => setPicked(new Set(eligible.filter(canFollow).map((l) => l.id)))}>
                {t("Select all")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPicked(new Set())}>{t("Clear")}</Button>
            </div>
            {/* Scrolls rather than growing: an inventory is not a list a dialog
                can be as tall as. */}
            <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {eligible.map((l) => (
                <label key={l.id} style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: '6px 8px',
                  borderRadius: 'var(--radius-sm)', cursor: canFollow(l) ? 'pointer' : 'default',
                  opacity: canFollow(l) ? 1 : 0.55,
                }}>
                  <Checkbox
                    checked={picked.has(l.id)}
                    disabled={!canFollow(l)}
                    onChange={() => toggle(l.id)}
                  />
                  <span style={{ font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)', flex: 1 }}>
                    {l.title}
                  </span>
                  <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
                    {canFollow(l) ? api.formatCents(l.priceCents) : t("Not in the catalogue")}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ) : null}

        <MarketFollowFields
          lowCents={null}
          avgCents={null}
          initial={{ followsMarket: true, adjust: null, basis: 'low' }}
          onChange={setMarket}
          note={t("Cards Cardmarket has no price for keep the price you set.")}
        />
      </div>
    </Dialog>
  );
}

export function YouScreen() {
  const { route, navigate, profile, myCards, myCardsState, loadMine, go, radius, meetups, trades, withdrawListing, withdrawListings, cursors, loadMore, loadingMore, wants, wantsState, wantsTotal, myCardsTotal, locale, t } = useApp();
  const [takingDown, setTakingDown] = React.useState(null);
  const [editing, setEditing] = React.useState(null);
  /*
   * YOUR SHOP, ASKED FOR ONCE.
   *
   * Read here rather than inside the two things that render it: the black
   * button sits in the header row and the invitation sits under the stats, and
   * two components asking the same question is two requests for one answer.
   * `undefined` is "not asked yet", which is what keeps either of them from
   * drawing anything before the answer arrives.
   */
  const [shop, setShop] = React.useState(undefined);
  React.useEffect(() => {
    let live = true;
    api.myShop().then((r) => { if (live) setShop(r.shop); }).catch(() => { if (live) setShop(null); });
    return () => { live = false; };
  }, []);
  const [bulkFollow, setBulkFollow] = React.useState(false);
  const confirmTakeDown = (listing) => setTakingDown(listing);

  /*
   * SEVERAL LISTINGS AT ONCE, the same way the wants list does it: held by id
   * because the grid grows under the selection with "Show more", and counted
   * from the cards actually on screen so that one leaving — withdrawn in
   * another tab, or by the refetch after this acts — stops being counted
   * without anything having to prune the set.
   */
  const cards = useSelection(myCards);
  const [takingDownMany, setTakingDownMany] = React.useState(false);
  const [withdrawing, setWithdrawing] = React.useState(false);
  const withdrawChosen = async (ids) => {
    setWithdrawing(true);
    try {
      await withdrawListings(ids);
      cards.clear();
      setTakingDownMany(false);
    } catch {
      // Said by the store. The cards are back on the grid, which is most of it.
    } finally { setWithdrawing(false); }
  };
  /*
   * The tab lives in the URL, not in state.
   *
   * It was read FROM the URL and never written back, so /you?tab=wants worked as a
   * typed link while clicking a tab changed nothing in the address bar — the back
   * button skipped straight off the screen, and there was no way to send somebody
   * the tab you were looking at. Half a feature reads as a broken one.
   *
   * A push rather than a replace: switching tabs is a deliberate move, so back
   * should return to the one you were on. Only typing gets collapsed, and there is
   * no typing here.
   */
  /*
   * A tab this screen does not have falls back to the first one.
   *
   * `?tab=saved` was a real, linkable URL until saving was taken out of the
   * product, so those links exist in people's history and bookmarks. Without
   * this they landed on Settings with nothing in the tab strip highlighted —
   * a screen that looks broken rather than one that has moved on.
   */
  const TABS = ['selling', 'wants', 'settings'];
  const tab = TABS.includes(route.tab) ? route.tab : 'selling';
  const setTab = (next) => navigate('you', { tab: next === 'selling' ? undefined : next });
  const mine = myCards;
  /*
   * THE SAME SELECTION BAR AS THE WANTS LIST, and deliberately the same in
   * every respect: at the top, stuck under the header, its space held whether
   * or not anything is ticked, and nothing inside it appearing or disappearing.
   * Two screens doing one job two ways is how a product comes to feel assembled
   * rather than designed.
   *
   * What differs is the verb. A want is deleted and says "Eliminar"; a listing
   * is only WITHDRAWN — it keeps its row, its photographs and the conversations
   * about it, and a revived import can bring it back — so this says "take
   * down", which is what the single card's own button has always said.
   *
   * And it asks before doing it, where the wants bar does not. The single
   * take-down has always asked, for a reason that only grows with the count:
   * there is no "put it back" from here, and reposting means filling the form
   * in again.
   *
   * Held as a variable because it is drawn in two of this tab's branches — over
   * the grid, and over the skeleton standing in for it. Rendered only once the
   * cards arrive, it would land ON them and shove the grid down, which is the
   * jump its reserved space exists to stop.
   */
  const selectionBar = (
    <BulkBar sticky idle={!cards.picked.size} count={cards.chosen.length} data-cbm="listing-actions">
      <Button
        variant="outline"
        disabled={!mine.length || cards.chosen.length === mine.length}
        onClick={cards.selectAll}
      >
        {t("Select all {n}", { n: mine.length })}
      </Button>
      <Button variant="outline" disabled={!cards.chosen.length} onClick={cards.clear}>
        {t("None")}
      </Button>
      <Button
        variant="outline" className="cbm-bulk__far"
        disabled={withdrawing || !cards.chosen.length} onClick={() => setTakingDownMany(true)}
      >
        {t("Take down {n}", { n: cards.chosen.length })}
      </Button>
    </BulkBar>
  );
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-5)', flexWrap: 'wrap' }}>
        <div className="cbm-avatar">{t("YOU")}</div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h1 className="cbm-h1" style={{ fontSize: 'var(--text-2xl)' }}>{profile.name}</h1>
          </div>
          <p className="cbm-sub">{profile.handle} · {t("trading within {radius}", { radius })}</p>
        </div>
        {/*
          Inviting somebody, beside posting a card.

          IT WAS AT THE BOTTOM OF SETTINGS and the owner could not find it —
          "I don't know how to see my invite code/referral page". Which is fair:
          it sat below the notification switches and the push diagnostics, on a
          tab nobody opens unless something is wrong. Inviting a friend is not
          a setting, it is a thing you do, and it belongs where the other thing
          you do already is.
        */}
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          {/* The shop, first in the row: it is a different account's door, and
              the two beside it are things you do as yourself. */}
          <ShopEntry shop={shop} />
          <Button variant="outline" iconLeft="share-2" onClick={() => go('invite')}>{t("Invite friends")}</Button>
          <Button iconLeft="plus" onClick={() => go('post')}>{t("Post a card")}</Button>
        </div>
      </div>
      <FluteDivider style={{ margin: 'var(--space-6) 0' }} />
      {/*
        Counted from the trades themselves rather than read off a profile object
        that never held them. `profile.trades` was hardcoded to 0 and
        `profile.rating` was hardcoded to null — so `rating.toFixed(1)` threw on
        every render and this screen has been blank for everybody who reached it.

        There is no rating any more: reputation is completed trades and a flag,
        decided by what people actually did. See api/src/lib/reputation.ts. A star
        score would be a number we do not have, which is how the null got here.
      */}
      <StatRow items={[
        { label: t("Trades done"), value: trades.filter((x) => x.status === 'completed').length },
        // The same number and the same reason — this said "50" too.
        /*
         * Shortened like the tab beside it. The two showing "5,2 mil" and
         * "5210" on one screen is the screen disagreeing with itself about the
         * same fact, which is worse than either choice on its own.
         *
         * Worth a second thought because this one is arguably where an exact
         * figure earns its place — it is your own inventory, and a seller
         * reconciling against Cardmarket wants the digits. If that turns out to
         * matter, this is the line to change back, not the rule.
         */
        { label: t("Listings live"), value: shortCount(myCardsTotal ?? mine.length, locale) },
        { label: t("Meetups agreed"), value: meetups.length },
      ]} />
      {/*
        A SHOP IS NOT A SETTING, which is where this lived until a shop became an
        account of its own: "the fact that shop related stuff lives in Settings
        is a bit weird now". A radius is a preference; a shop is a second thing
        you are, with its own page, its own stock and its own name on a trade.

        What is left here is the OFFER, for somebody who has no shop. Anybody
        who has one reaches it from the button in the header — see `ShopEntry`.
      */}
      <ShopInvite shop={shop} />
      <Tabs label={t("Your profile")} value={tab} onChange={setTab} style={{ margin: 'var(--space-7) 0 var(--space-6)' }} items={[
        /*
         * HOW MANY THERE ARE, not how many are loaded.
         *
         * `mine.length` is one page. Somebody who imported five thousand
         * listings was told "50", which is the size of the fetch rather than
         * anything about them. The server sends a total on the first page and
         * the store holds it; this reads that.
         */
        /*
         * Shortened past a thousand — see `shortCount`. A seller who imported
         * five thousand cards was being shown "5000", which is four digits
         * nobody reads as a quantity; "5 mil" is the same fact in the shape
         * people actually think in, and in the locale's own convention rather
         * than an English K.
         */
        { value: 'selling', label: t("Selling"), count: myCardsTotal == null ? undefined : shortCount(myCardsTotal, locale) },
        /*
         * The wants count, beside the selling one it always sat next to.
         *
         * WITHHELD WHILE THEY ARE LOADING rather than shown as nought. The
         * wants are fetched separately from the listings, so a zero here would
         * be the honest state of the array and a lie about the person — and
         * "Wants 0" beside "Selling 11" reads as "you want nothing", which is
         * exactly the wrong thing to tell somebody who has 249 of them and a
         * slow connection.
         */
        { value: 'wants', label: t("Wants"), count: wantsTotal == null ? undefined : shortCount(wantsTotal, locale) },
        { value: 'settings', label: t("Settings") },
      ]} />
      {/*
        Importing an inventory, offered on SELLING and nowhere else.

        ON WANTS TOO, SINCE 2026-09-23. This said "not on Wants... our API has
        no wants import at all, so the button would lead somewhere that cannot
        do the thing it offers" — which was true when it was written and stopped
        being true the day the wants import shipped. Reported by the owner, who
        noticed the offer was missing from the one tab it belongs on.

        Each tab offers ITS OWN import and says which: "Import inventory" on
        Selling, "Import your wants" on Wants. The two files look almost
        identical in a downloads folder, and a single button saying "Import"
        would put that confusion back one screen later — which is exactly what
        the import screen's first step exists to prevent.

        Above the tab's content rather than inside one of its branches, because
        it is worth having in all four of them — an import is most useful to
        somebody whose shelf is empty, and that branch is the one that says
        "Nothing listed yet".
      */}
      {tab === 'wants' ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-4)', marginBottom: 'var(--space-5)' }}>
          {/*
            Straight to the wants import, not to the question of which file.
            Somebody who pressed this has already answered it.
          */}
          <Button variant="outline" size="sm" iconLeft="upload"
                  onClick={() => go('import', { kind: IMPORT_SOURCES.wants })}>
            {t("Import your wants")}
          </Button>
        </div>
      ) : null}
      {tab === 'selling' ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-4)', marginBottom: 'var(--space-5)' }}>
          {/*
            Beside the import, and that is where it belongs: the two are the
            same moment in a seller's day. Somebody who has just brought three
            hundred cards in is the person who wants them all tracking the
            market, and the per-listing switch cannot be asked three hundred
            times.

            Only where there is something to apply it to. On an empty shelf it
            would be a button that opens a dialog listing nothing.
          */}
          {mine.length ? (
            <Button variant="outline" size="sm" iconLeft="refresh-cw" onClick={() => setBulkFollow(true)}>
              {t("Follow Cardmarket's price")}
            </Button>
          ) : null}
          <Button variant="outline" size="sm" iconLeft="upload" onClick={() => go('import', { kind: IMPORT_SOURCES.stock })}>{t("Import inventory")}</Button>
        </div>
      ) : null}
      {tab === 'selling' ? (
        myCardsState === 'error' ? (
          <EmptyState icon="alert-triangle" title={t("We could not load your listings")}
            description={t("Something went wrong at our end. They are still there.")}
            action={<Button onClick={() => loadMine()}>{t("Try again")}</Button>} />
        ) : mine.length ? (
          <>
          {selectionBar}
          {/* The control sits OUTSIDE the grid. Inside it, it becomes a grid
              cell — a full-width button wedged among the cards instead of
              centred beneath them. */}
          <div className="cbm-grid-listings">{mine.map((c) => (
          /* `data-listing` so a browser test can act on ONE of these rather than
             on whichever happens to be first — which is how a test ends up
             asserting about a listing it did not mean. Nothing reads it at
             runtime. A plain block comment, not `{\/* *\/}`: this position is
             inside an arrow's parenthesised expression, where JSX comment
             syntax is not valid and the build says so. */
          <div
            key={c.id} data-listing={c.id}
            data-picked={cards.picked.has(c.id) ? '1' : undefined}
            /* The gesture is read here, before the card or its link sees it —
               see `useSelection`. With a selection live the whole cell answers
               to it, which is what makes shift-click and "tap anywhere" work;
               with none, only the tick box does, and the card is a card. */
            onClickCapture={(e) => cards.press(c.id, e)}
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', cursor: cards.selecting ? 'pointer' : undefined }}>
            {/* The click guard used to be `c.id.startsWith('l')`, left over from
                mock data with ids like "l1". Real ids are uuids, so clicking your
                own listing did nothing at all. */}
            {/* NOT `km ?? 0`. Zero means "here", and DistanceTag prints anything
                under 1 km as "less than 1 km" — so an unknown distance was being
                shown as the smallest possible one. Passing undefined makes the tag
                draw nothing, which is what its own comment says it is for: not
                knowing a distance is not the same as knowing it is small. */}
            {/*
              THE TICK BOX SITS OVER THE CARD, not in the column with it.

              A grid cell has no 18px decoration to trade for a control the way
              a wants row did, and anything added to the flow would retune the
              cell's height — on the one screen where every cell must stay the
              same height as the two beside it. An overlay costs no layout at
              all: the card is drawn exactly as it was, with the box on top of
              its corner.

              Its own chip of white behind it, because what it overlays is
              somebody's photograph and a bare box on a dark card is invisible.

              A SIBLING OF THE LINK RATHER THAN INSIDE IT. Nested in the anchor,
              every tick would also be a navigation; beside it and on top, the
              tap lands on the label and the card underneath never hears it.

              The hidden name is the card's own title — the same reasoning as
              the wants row, and the same reason it cannot be an `aria-label`:
              the design system's Checkbox spreads what it is given onto its own
              label element, where ARIA does not reach the input.
            */}
            <div className="cbm-card-slot" style={{ position: 'relative' }}>
              <Linked to={{ name: 'listing', id: c.id }}>
                <ListingCard {...api.listingCardProps(c, t)} />
              </Linked>
              <label className="cbm-pick-card">
                {/* Drawn from the selection, never from its own event: the press
                    on the cell owns the gesture, so this is told what it is. */}
                <Checkbox checked={cards.picked.has(c.id)} onChange={() => {}} />
                <span className="cbm-pick__name">{t("Select {card}", { card: c.title })}</span>
              </label>
            </div>
            {/*
              The stock, on the screen where a seller manages it — ALWAYS, even
              at one copy.

              This was hidden below two, on the grounds that "1 copy" under every
              card is a row of numbers saying nothing. True about the words and
              wrong about the column: the cards sit in a grid, so a card missing
              this line pulls its own buttons up by a row and sits out of step
              with the two beside it. Not a flicker that settles — a permanent
              misalignment, on the screen a seller looks at most.

              And the word is not nothing either. In a marketplace where somebody
              may want three, "1 copia" is the answer to a real question, and its
              absence was only ever readable as an answer if you knew the rule.

              Committed is the figure that decides whether a reduction will be
              refused, so it is shown before somebody tries rather than after.
            */}
            {/*
              METADATA, and now visibly so: it sits with the photo count on one
              muted line rather than looking like another button in the row
              below. Both answer "what is this listing", which is a different
              question from "what can I do to it".
            */}
            <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
              {(c.stock || {}).committed > 0
                ? t("{available} of {quantity} free · {committed} promised", { available: c.stock.available, quantity: c.stock.quantity, committed: c.stock.committed })
                : ((c.stock || {}).quantity ?? 1) === 1
                  ? t("One copy")
                  : t("{quantity} copies", { quantity: c.stock.quantity })}
            </span>
            {/*
              WHICH LISTINGS PRICE THEMSELVES, at a glance.
              
              REPORTED: "it would be nice to be able to distinguish easily which
              ones have the automatic price sync enabled and which ones do not.
              Maybe an icon or something."
              
              AN ICON AND THE RULE, rather than an icon alone. A bare marker
              answers "is it on" and leaves the next question — on WHAT — to a
              dialog somebody has to open, and after a bulk change across three
              hundred listings that is the question. "Cardmarket · bajo −5%" is
              barely longer and answers both.
              
              The same icon as the bulk button above the grid, so the mark and
              the control that sets it are recognisably the same thing.
            */}
            {/* Not for a synced card: the nightly reprice skips it, so the rule is not what prices it. */}
            {c.pricing && c.pricing.followsMarket && !c.pricing.managedOnCardtrader ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
                <Icon name="refresh-cw" size={13} />
                {marketRuleText(c.pricing, t)}
              </span>
            ) : null}
            {/*
              TWO ACTIONS ON ONE LINE, and they look like actions.
              
              REPORTED, looking at his own Selling tab: "buttons do not align
              well mixed with simple labels. And at this point it doesn't make
              sense to maintain different buttons for Edit listing and Upload
              photos."
              
              Both halves were true. Three ghost buttons have no edges, so they
              read as text at the same weight as the copies line above them —
              except indented differently, because a button carries padding a
              span does not. Nothing lined up with anything, on the screen a
              seller looks at most.
              
              So: one OUTLINED button, which is visibly a control, and one ghost
              beside it for the destructive one — a border on "take it down"
              would give the loudest affordance to the action nobody is looking
              for. Photos moved inside the edit dialog, where they were always
              the same intent.
            */}
            <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
              {/* A synced card opens its details, not an editor (ticket 11): the
                  button says which, so it does not promise an edit it cannot keep. */}
              {c.pricing && c.pricing.managedOnCardtrader ? (
                <Button size="sm" variant="outline" iconLeft="eye" onClick={() => setEditing(c)}>
                  {t("Details")}
                </Button>
              ) : (
                <Button size="sm" variant="outline" iconLeft="pencil" onClick={() => setEditing(c)}>
                  {t("Edit")}
                </Button>
              )}
              {/* Pushed to the far end rather than sitting next to Edit: it is
                  not the next thing after editing, and a hand reaching for one
                  should not find the other. */}
              <Button size="sm" variant="ghost" style={{ marginLeft: 'auto' }} onClick={() => confirmTakeDown(c)}>
                {t("Take it down")}
              </Button>
            </div>
          </div>
        ))}
          </div>
          <LoadMore cursor={cursors.mine} busy={loadingMore === 'mine'} onClick={() => loadMore('mine')} />
          </>
        )
          /*
           * Loading is not "nothing listed yet", and this branch did not exist.
           *
           * The same mistake the browse error branch was written to stop, one
           * state along: while the first page was in flight this told a seller
           * their stock was gone and offered them the posting form. Now it holds
           * the grid's shape until the answer arrives, and only then does an
           * empty grid mean an empty shelf.
           */
          : myCardsState === 'loading' ? <>{selectionBar}<ListingGridSkeleton label={t("Loading your listings.")} /></>
          : <EmptyState icon="layers" title={t("Nothing listed yet")} description={t("Post a card and traders inside your radius see it straight away.")} action={<Button onClick={() => go('post')}>{t("Post a card")}</Button>} />
      ) : tab === 'wants' ? <WantsPanel />
        : <SettingsPanel />}

      {/* Asked before doing, because it cannot be undone from here — a withdrawn
          listing has no "put it back" button, and reposting means filling the form
          again. Not a heavy warning either: taking your own card off the market is
          an ordinary thing to want. */}
      {/* The shared confirmation (chrome.jsx), as every destructive action has. */}
      {takingDown ? (
        <ConfirmDialog
          title={t("Take down “{card}”?", { card: takingDown.title })}
          description={t("It stops showing to other traders. Any conversation about it stays where it is.")}
          confirmLabel={t("Take it down")}
          onCancel={() => setTakingDown(null)}
          onConfirm={() => { withdrawListing(takingDown.id); setTakingDown(null); }}
        />
      ) : null}
      {takingDownMany ? (
        <ConfirmDialog data-cbm="take-down-many"
          title={t("Take down {n} listings?", { n: cards.chosen.length })}
          description={t("They stop showing to other traders. Any conversation about them stays where it is.")}
          confirmLabel={t("Take them down")} busyLabel={t("One moment…")} busy={withdrawing}
          items={cards.chosen.map((c) => c.title)}
          onCancel={() => setTakingDownMany(false)}
          onConfirm={() => withdrawChosen(cards.chosen.map((c) => c.id))}
        />
      ) : null}
      <ModalHost>
        {editing ? (
          <EditListingDialog listing={editing} onClose={() => setEditing(null)} />
        ) : null}
        {bulkFollow ? (
          <BulkFollowDialog listings={mine} onClose={() => setBulkFollow(false)} />
        ) : null}
      </ModalHost>
    </>
  );
}

